/**
 * GenericCase.jsx — Refonte Premium
 * ─────────────────────────────────────────────────────────────────────────────
 * 4 systèmes nouveaux, rétrocompatibles avec le store usePesa et l'engine PESACORE :
 *
 *  [1] ContextStream      — fil narratif gauche, push automatique par phase
 *  [2] ProbabilityBridge  — narration visuelle de l'écart théorique / réel
 *  [3] InsightOverlay     — flash contextuel post-action (non-bloquant, 4s)
 *  [4] DebriefOnboarding  — 3 slides d'introduction avant RapportEnquete
 *
 * Exports conservés (compatibilité) :
 *   DynamicsPanel, PredictionPanel, HistoryPanel, SolutionsPanel,
 *   CounterfactualsPanel, CausalPanel, TheoryPanelSide, RadialParameters,
 *   PESAField, PESAFieldOverlay, LiveFieldModal, useActionPulse,
 *   PulsingNavButton
 *
 * Export default : GenericCase (page principale)
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Box,
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Crosshair,
  Factory,
  FileText,
  GitBranch,
  HelpCircle,
  History,
  Lightbulb,
  Maximize2,
  Minimize2,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  School,
  Shield,
  Shrink,
  Sparkles,
  Sun,
  Target,
  TrendingDown,
  TrendingUp,
  Volume2,
  VolumeX,
  X,
  Zap
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Radar } from 'react-chartjs-2';
import { createPortal } from 'react-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import SignalVisualizer from '../components/SignalVisualizer';
import { K2000VoiceScanner } from '../components/Simulations/K2000VoiceScanner';
import RapportEnquete from '../components/Simulations/RapportEnquete';
import api from '../config/axiosConfig';
import { useTheme } from '../contexts/ThemeContext';
import { usePesa } from '../stores/usePesaEngine';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS 
// ─────────────────────────────────────────────────────────────────────────────

const T = {
  // Couleurs primaires du système
  ink:       '#EDE9E1',
  inkMuted:  'rgba(237,233,225,0.45)',
  inkGhost:  'rgba(237,233,225,0.18)',
  gold:      '#C9A55A',
  goldSoft:  'rgba(201,165,90,0.15)',
  goldGlow:  'rgba(201,165,90,0.35)',
  violet:    '#9B7FE8',
  violetSoft:'rgba(155,127,232,0.12)',
  emerald:   '#34D399',
  red:       '#F87171',
  amber:     '#FBBF24',
  slate:     'rgba(255,255,255,0.06)',
  // Surfaces
  surface0:  'rgba(8,7,14,0.97)',
  surface1:  'rgba(14,13,22,0.92)',
  surface2:  'rgba(22,20,34,0.85)',
  border0:   'rgba(255,255,255,0.05)',
  border1:   'rgba(255,255,255,0.10)',
  border2:   'rgba(255,255,255,0.16)',
};

const MONO = `text-[9px] font-mono tracking-[0.22em] uppercase`;
const MONO_SM = `text-[8px] font-mono tracking-[0.18em] uppercase`;

const FAMILY_COLORS = {
  École: '#3b82f6', Travail: '#ef4444', Justice: '#6366f1',
  Art: '#f59e0b', Médiation: '#10b981', Famille: '#8b5cf6', Santé: '#ec4899',
};

const AMBIANCE_PALETTE = {
  tension:    { bg: 'rgba(239,68,68,0.47)',   link: 'rgba(239,68,68,0.08)',   particle: '#ef4444', glow: '#7f1d1d' },
  hope:       { bg: 'rgba(16,185,129,0.41)',  link: 'rgba(16,185,129,0.08)',  particle: '#10b981', glow: '#064e3b' },
  crisis:     { bg: 'rgba(245,159,11,0.32)',  link: 'rgba(245,158,11,0.10)',  particle: '#f59e0b', glow: '#78350f' },
  resolution: { bg: 'rgba(99,101,241,0.4)',   link: 'rgba(99,102,241,0.06)',  particle: '#818cf8', glow: '#1e1b4b' },
  default:    { bg: 'rgba(169,85,247,0.53)',  link: 'rgba(255,255,255,0.03)', particle: '#a855f7', glow: '#2e1065' },
};

const ARCHETYPE_ORBIT = {
  opprimé:      { speed: 0.8,  radius: 1.0, pulse: true,  color: '#ef4444' },
  reproducteur: { speed: 0.3,  radius: 1.2, pulse: false, color: '#6366f1' },
  médiateur:    { speed: 0.6,  radius: 1.1, pulse: true,  color: '#10b981' },
  subversif:    { speed: 1.4,  radius: 0.9, pulse: true,  color: '#f59e0b' },
  institution:  { speed: 0.15, radius: 1.4, pulse: false, color: '#94a3b8' },
};

const TENSION_CONFIG = {
  1: { chaos: 0,    nodeJitter: 0,    linkOpacity: 0.04 },
  2: { chaos: 0.02, nodeJitter: 0.5,  linkOpacity: 0.06 },
  3: { chaos: 0.05, nodeJitter: 1.2,  linkOpacity: 0.08 },
  4: { chaos: 0.10, nodeJitter: 2.5,  linkOpacity: 0.12 },
  5: { chaos: 0.18, nodeJitter: 4.0,  linkOpacity: 0.18 },
};

const LEVEL_COLORS_CAUSAL = {
  débutant:      'border-blue-500/25   bg-blue-950/15   text-blue-300',
  intermédiaire: 'border-emerald-500/25 bg-emerald-950/15 text-emerald-300',
  avancé:        'border-amber-500/25  bg-amber-950/15  text-amber-300',
  expert:        'border-violet-500/25 bg-violet-950/15 text-violet-300',
};

// ─────────────────────────────────────────────────────────────────────────────
// PRIMITIVES PARTAGÉES
// ─────────────────────────────────────────────────────────────────────────────

const StatusTag = ({ children, color = 'purple' }) => {
  const map = {
    purple:  'bg-violet-500/10 border-violet-500/20 text-violet-400',
    amber:   'bg-amber-500/10  border-amber-500/20  text-amber-400',
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    red:     'bg-red-500/10    border-red-500/20    text-red-400',
    slate:   'bg-white/5       border-white/10      text-white/40',
    gold:    'bg-[#C9A55A]/10  border-[#C9A55A]/25  text-[#C9A55A]',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[9px] font-mono tracking-widest ${map[color] || map.slate}`}>
      {children}
    </span>
  );
};

const LabGrid = ({ darkMode }) => (
  <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.012]">
    <div className="absolute inset-0" style={{
      backgroundImage: `linear-gradient(${darkMode ? '#ffffff' : '#000000'} 1px, transparent 1px),linear-gradient(90deg, ${darkMode ? '#ffffff' : '#000000'} 1px, transparent 1px)`,
      backgroundSize: '80px 80px',
    }} />
  </div>
);

const Scanlines = () => (
  <div className="fixed inset-0 pointer-events-none z-[1] opacity-[0.01]">
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-transparent h-2 animate-[scanline_8s_ease-in-out_infinite]" />
  </div>
);

const SoberWrapper = ({ children, className = '', highlight = false }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: -6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6, scale: 0.98 }}
    transition={{ duration: 0.2, ease: 'easeOut' }}
    className={`rounded-xl overflow-hidden backdrop-blur-md ${className}`}
    style={{
      background: highlight ? 'rgba(201,165,90,0.06)' : T.surface2,
      border: `1px solid ${highlight ? T.goldGlow : T.border0}`,
      boxShadow: highlight ? `0 0 20px rgba(201,165,90,0.08)` : 'none',
      transition: 'border-color 0.4s ease, box-shadow 0.4s ease',
    }}
  >
    {children}
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// [1] CONTEXT STREAM — fil narratif gauche, push auto
// ─────────────────────────────────────────────────────────────────────────────

/**
 * useContextStream — détermine quelles cartes afficher selon la phase courante,
 * le niveau de tension et les actions récentes. Pas de clics requis.
 */
const useContextStream = () => {
  const { engine, currentPhase, currentStep, completedActions, phase } = usePesa();
  const [stream, setStream] = useState([]);
  const prevPhaseRef = useRef(null);
  const prevActionCountRef = useRef(0);

  const completedArr = completedActions instanceof Set ? [...completedActions] : [];
  const tensionLevel = currentStep?.narrativeArc?.tensionLevel ?? 2;
  const ambiance     = currentPhase?.ambiance || 'default';
  const isAction     = currentPhase?.type === 'action';

  useEffect(() => {
    const cards = [];

    // Toujours : carte de phase courante
    if (currentPhase) {
      cards.push({ id: 'phase', type: 'phase', priority: 0 });
    }

    // Tension ≥ 3 : carte tension automatique
    if (tensionLevel >= 3) {
      cards.push({ id: 'tension', type: 'tension', priority: 1 });
    }

    // Phase action : carte hint si disponible
    if (isAction && currentPhase?.hint) {
      cards.push({ id: 'hint', type: 'hint', priority: 2 });
    }

    // Ancrage théorique si disponible
    if (currentPhase?.theoreticalHook) {
      cards.push({ id: 'theory', type: 'theory', priority: 3 });
    }

    // Après une action jouée : causal + prédiction
    if (completedArr.length > 0) {
      cards.push({ id: 'causal', type: 'causal', priority: 4 });
      cards.push({ id: 'prediction', type: 'prediction', priority: 5 });
    }

    // Phase terminée : contrefactuels
    if (completedArr.length > 1) {
      cards.push({ id: 'counterfactuals', type: 'counterfactuals', priority: 6 });
    }

    // Fin de simulation : carte bilan
    if (phase === 'finished') {
      cards.push({ id: 'bilan', type: 'bilan', priority: 7 });
    }

    setStream(cards.sort((a, b) => a.priority - b.priority));
    prevPhaseRef.current = currentPhase?.type;
    prevActionCountRef.current = completedArr.length;
  }, [
    currentPhase?.type, tensionLevel, isAction,
    completedArr.length, phase, currentPhase?.theoreticalHook, currentPhase?.hint,
  ]);

  return stream;
};

// Carte : phase courante
const StreamCardPhase = ({ currentPhase, currentStep, tensionLevel }) => {
  const ambianceColors = {
    tension:    { text: 'text-red-400',     dot: 'bg-red-400',     border: 'border-red-500/20'     },
    hope:       { text: 'text-emerald-400', dot: 'bg-emerald-400', border: 'border-emerald-500/20' },
    crisis:     { text: 'text-amber-400',   dot: 'bg-amber-400',   border: 'border-amber-500/20'   },
    resolution: { text: 'text-indigo-400',  dot: 'bg-indigo-400',  border: 'border-indigo-500/20'  },
    default:    { text: 'text-violet-400',  dot: 'bg-violet-400',  border: 'border-violet-500/20'  },
  };
  const c = ambianceColors[currentPhase?.ambiance || 'default'];

  return (
    <div className="p-4 space-y-3">
      <div className={`flex items-center gap-2 pb-3 border-b ${c.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${c.dot}`} />
        <span className={`${MONO} ${c.text}`}>{currentPhase?.type || 'INIT'}</span>
        {currentPhase?.title && (
          <span className={`ml-auto ${MONO_SM} text-white/30 truncate max-w-[120px]`}>
            {currentPhase.title}
          </span>
        )}
      </div>
      <p className="text-[12px] text-white/70 leading-relaxed">
        {currentStep?.title || 'Analyse en cours…'}
      </p>
      {currentStep?.narrativeArc?.position && (
        <StatusTag color="slate">{currentStep.narrativeArc.position}</StatusTag>
      )}
    </div>
  );
};

// Carte : tension
const StreamCardTension = ({ tensionLevel }) => {
  const labels = ['', 'STABLE', 'ACTIF', 'TENDU', 'CRITIQUE', 'RUPTURE'];
  const colors = ['', '#64748b', '#3b82f6', '#f59e0b', '#f97316', '#ef4444'];
  const c = colors[tensionLevel] || '#a78bfa';
  return (
    <div className="p-4 space-y-3">
      <div className={`${MONO} text-white/25 mb-2`}>Niveau de tension</div>
      <div className="flex items-end gap-1.5">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="flex-1 rounded-sm transition-all duration-500"
            style={{
              height: `${10 + i * 4}px`,
              background: i <= tensionLevel ? c : 'rgba(255,255,255,0.06)',
              boxShadow: i <= tensionLevel ? `0 0 8px ${c}60` : 'none',
            }}
          />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono font-bold" style={{ color: c }}>
          {labels[tensionLevel]}
        </span>
        <span className={`${MONO_SM} text-white/20`}>T{tensionLevel}/5</span>
      </div>
    </div>
  );
};

// Carte : hint
const StreamCardHint = ({ hint }) => (
  <div className="p-4 flex items-start gap-2.5">
    <Lightbulb size={11} className="text-amber-400/70 mt-0.5 shrink-0" />
    <p className="text-[11px] text-amber-200/70 leading-relaxed italic">{hint}</p>
  </div>
);

// Carte : ancrage théorique
const StreamCardTheory = ({ hook }) => (
  <div className="p-4 space-y-2">
    <div className="flex items-center gap-1.5">
      <BookOpen size={10} className="text-indigo-400/60" />
      <span className={`${MONO} text-indigo-400/50`}>Ancrage théorique</span>
    </div>
    <p className="text-[11px] text-indigo-200/75 leading-relaxed italic">{hook}</p>
  </div>
);

// Carte : causal inline (version compacte)
const StreamCardCausal = () => {
  const { engine, completedActions } = usePesa();
  const completedArr = completedActions instanceof Set ? [...completedActions] : [];
  const lastActionId = completedArr[completedArr.length - 1] || null;
  const lastAction = useMemo(() => {
    if (!lastActionId) return null;
    return engine?.case?.steps?.flatMap(s => s.actions || []).find(a => a.id === lastActionId) || null;
  }, [lastActionId, engine?.case?.steps]);

  const cc = lastAction?.causalChain || [];
  const df = lastAction?.deepFeedback;

  if (!lastAction) return null;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-1.5">
        <GitBranch size={10} className="text-violet-400/60" />
        <span className={`${MONO} text-violet-400/50`}>Effet causal</span>
      </div>
      {df?.whatHappened && (
        <p className="text-[11px] text-white/60 leading-relaxed">{df.whatHappened}</p>
      )}
      {df?.whyItMatters && (
        <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg" style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.12)' }}>
          <TrendingUp size={9} className="text-amber-400/60 shrink-0 mt-0.5" />
          <p className="text-[10px] text-amber-200/65 leading-relaxed italic">{df.whyItMatters}</p>
        </div>
      )}
      {cc.slice(0, 2).map((link, i) => (
        <div key={i} className="flex items-center gap-2 text-[10px]">
          <span className="text-white/35 truncate">{link.from}</span>
          <ArrowRight size={8} className="text-white/15 shrink-0" />
          <span className="text-emerald-400/70 truncate">{link.to}</span>
        </div>
      ))}
    </div>
  );
};

// Carte : prédiction inline
const StreamCardPrediction = () => {
  const { engine, phase, currentSolution } = usePesa();
  let proba = 0;
  if (phase === 'finished' && currentSolution?.interpretation) {
    proba = currentSolution.interpretation.realWorldProbability ?? currentSolution.interpretation.theoreticalPromise ?? 10;
  } else {
    proba = engine?.state?.optimalPath?.probabilityOfSuccess ?? engine?.state?.prediction?.probability ?? 0;
  }
  const pct = Math.max(0, Math.min(100, proba > 1 ? proba : proba * 100));
  const color = pct >= 75 ? T.emerald : pct >= 50 ? T.amber : T.red;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Activity size={10} className="text-white/30" />
          <span className={`${MONO} text-white/25`}>Probabilité</span>
        </div>
        <span className="text-xl font-bold font-mono tabular-nums" style={{ color }}>
          {pct.toFixed(1)}<span className="text-sm text-white/30">%</span>
        </span>
      </div>
      <div className="h-1 rounded-full overflow-hidden bg-white/5">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{ background: color, boxShadow: `0 0 8px ${color}60` }}
        />
      </div>
      <p className={`${MONO_SM} text-white/20`}>
        {phase === 'finished' ? 'Données historiques réelles' : 'Optimisation génétique'}
      </p>
    </div>
  );
};

// Carte : contrefactuels compact
const StreamCardCounterfactuals = () => {
  const { engine } = usePesa();
  const cfs = (engine?.counterfactuals || [])
    .sort((a, b) => b.deltaProbability - a.deltaProbability)
    .slice(0, 3);

  if (!cfs.length) return null;

  return (
    <div className="p-4 space-y-2.5">
      <div className="flex items-center gap-1.5">
        <Factory size={10} className="text-violet-400/60" />
        <span className={`${MONO} text-violet-400/50`}>Leviers manqués</span>
      </div>
      {cfs.map((cf, i) => (
        <div key={i} className="flex items-center gap-2.5">
          <span className="text-[9px] font-mono font-bold text-violet-400/70 w-8 shrink-0">
            +{(cf.deltaProbability * 100).toFixed(0)}%
          </span>
          <span className="text-[10px] text-white/45 truncate">{cf.actionTitle}</span>
        </div>
      ))}
    </div>
  );
};

// Carte : bilan fin
const StreamCardBilan = ({ currentSolution }) => {
  if (!currentSolution) return null;
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <CheckCircle2 size={12} className="text-emerald-400 shrink-0" />
        <span className={`${MONO} text-emerald-400/70`}>Simulation résolue</span>
      </div>
      <p className="text-[12px] font-semibold text-white/80">{currentSolution.name}</p>
      <p className="text-[11px] text-white/45 leading-relaxed line-clamp-3">{currentSolution.description}</p>
    </div>
  );
};

/**
 * ContextStream — remplace la colonne gauche.
 * Affiche un fil de cartes déterminé par useContextStream().
 */
export const ContextStream = () => {
  const {
    engine, currentPhase, currentStep, phase,
    currentSolution, scenario,
  } = usePesa();
  const stream = useContextStream();
  const tensionLevel = currentStep?.narrativeArc?.tensionLevel ?? 2;

  // Label de section par priorité
  const sectionLabel = stream.length > 0 ? 'Contexte en cours' : 'En attente…';

  const CARD_COMPONENTS = {
    phase:           () => <StreamCardPhase currentPhase={currentPhase} currentStep={currentStep} tensionLevel={tensionLevel} />,
    tension:         () => <StreamCardTension tensionLevel={tensionLevel} />,
    hint:            () => currentPhase?.hint ? <StreamCardHint hint={currentPhase.hint} /> : null,
    theory:          () => currentPhase?.theoreticalHook ? <StreamCardTheory hook={currentPhase.theoreticalHook} /> : null,
    causal:          () => <StreamCardCausal />,
    prediction:      () => <StreamCardPrediction />,
    counterfactuals: () => <StreamCardCounterfactuals />,
    bilan:           () => <StreamCardBilan currentSolution={currentSolution} />,
  };

  // Couleur de bordure gauche par type
  const borderColors = {
    phase: T.violet, tension: T.red, hint: T.gold,
    theory: '#818cf8', causal: T.violet, prediction: T.emerald,
    counterfactuals: T.violet, bilan: T.emerald,
  };

  return (
    <div className="flex flex-col h-full overflow-hidden p-5 gap-4">
      {/* En-tête */}
      <div className="shrink-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500" />
          </span>
          <span className={`${MONO} text-amber-400/60`}>Flux contextuel</span>
        </div>
        <p className="text-[11px] text-white/30 font-mono">{sectionLabel}</p>
      </div>

      {/* Fil de cartes */}
      <div className="flex-1 overflow-y-auto space-y-3 scrollbar-thin scrollbar-thumb-white/5 scrollbar-track-transparent pr-1">
        <AnimatePresence mode="popLayout">
          {stream.map((card) => {
            const Component = CARD_COMPONENTS[card.type];
            if (!Component) return null;
            const content = Component();
            if (!content) return null;
            return (
              <motion.div
                key={card.id}
                layout
                initial={{ opacity: 0, x: -12, scale: 0.97 }}
                animate={{ opacity: 1,  x: 0,   scale: 1 }}
                exit={{ opacity: 0, x: -12, scale: 0.97 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className="rounded-xl overflow-hidden"
                style={{
                  background: T.surface2,
                  borderLeft: `2px solid ${borderColors[card.type] || T.border1}`,
                  border: `1px solid ${T.border0}`,
                  borderLeftColor: borderColors[card.type] || T.border1,
                }}
              >
                {content}
              </motion.div>
            );
          })}
        </AnimatePresence>

        {stream.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
            <div className="w-8 h-8 rounded-full border border-white/8 flex items-center justify-center">
              <Activity size={14} className="text-white/20 animate-pulse" />
            </div>
            <p className={`${MONO} text-white/15`}>Initialisation…</p>
          </div>
        )}
      </div>

      {/* Footer : progression scénario */}
      {scenario?.steps && (
        <div className="shrink-0 pt-3 border-t border-white/5">
          <div className="flex items-center justify-between mb-2">
            <span className={`${MONO_SM} text-white/20`}>Progression</span>
            <span className={`${MONO_SM} text-white/30`}>
              {(engine?.state?.currentStepIndex || 0) + 1}/{scenario.steps.length}
            </span>
          </div>
          <div className="h-0.5 rounded-full bg-white/5 overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{
                background: 'linear-gradient(90deg, #7c3aed, #C9A55A)',
                width: `${Math.round(((engine?.state?.currentStepIndex || 0) / Math.max(scenario.steps.length - 1, 1)) * 100)}%`,
              }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// [2] PROBABILITY BRIDGE — narration visuelle de l'écart théorique / réel
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ProbabilityBridge
 * Deux barres superposées : théorique (violet) vs réel (ambre).
 * Un badge narratif explique l'écart avec une référence historique.
 * S'affiche dans le header central, visible sans aucun clic.
 */
export const ProbabilityBridge = () => {
  const { engine, phase, currentSolution } = usePesa();
  const [showExplainer, setShowExplainer] = useState(false);

  // Probabilité théorique (optimisation génétique)
  const theoretical = useMemo(() => {
    const raw = engine?.state?.optimalPath?.probabilityOfSuccess
      ?? engine?.state?.prediction?.probability
      ?? 0;
    return Math.max(0, Math.min(100, raw > 1 ? raw : raw * 100));
  }, [engine?.state]);

  // Probabilité réelle (historique)
  const real = useMemo(() => {
    if (phase === 'finished' && currentSolution?.interpretation) {
      const r = currentSolution.interpretation.realWorldProbability
        ?? currentSolution.interpretation.theoreticalPromise
        ?? 10;
      return Math.max(0, Math.min(100, r > 1 ? r : r * 100));
    }
    // Avant fin : on simule l'écart habituel (~40% de réduction)
    return Math.round(theoretical * 0.58);
  }, [phase, currentSolution, theoretical]);

  const gap      = theoretical - real;
  const gapColor = gap > 30 ? T.red : gap > 15 ? T.amber : T.emerald;

  // Références historiques selon le niveau de gap
  const historicalNote = useMemo(() => {
    if (gap > 40) return 'Bourdieu (1970) : 88% des tentatives d\'émancipation scolaire ont été réabsorbées par la reproduction sociale.';
    if (gap > 25) return 'Foucault (1975) : les dispositifs institutionnels neutralisent 70% des transformations initiées par la base.';
    if (gap > 10) return 'Crozier & Friedberg (1977) : les acteurs stratégiques ajustent leurs résistances à l\'ampleur du changement.';
    return 'Weber (1922) : quand la légitimité est construite, la transformation peut s\'institutionnaliser durablement.';
  }, [gap]);

  if (theoretical === 0) return null;

  return (
    <>
      <button
        onClick={() => setShowExplainer(true)}
        className="flex flex-col gap-2 group cursor-pointer"
        title="Comprendre l'écart théorique / réel"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <span className={`${MONO_SM} text-white/25`}>Δ Théorique / Réel</span>
          <div className="flex items-center gap-1.5">
            <span
              className={`${MONO_SM} font-bold tabular-nums`}
              style={{ color: gapColor }}
            >
              −{gap.toFixed(0)}%
            </span>
            <HelpCircle size={9} className="text-white/20 group-hover:text-white/50 transition-colors" />
          </div>
        </div>

        {/* Double barre */}
        <div className="space-y-1.5">
          {/* Théorique */}
          <div className="relative">
            <div className="h-1 rounded-full bg-white/5 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${theoretical}%` }}
                transition={{ duration: 1.2, ease: 'easeOut' }}
                className="h-full rounded-full"
                style={{ background: T.violet, boxShadow: `0 0 6px ${T.violet}60` }}
              />
            </div>
            <div className="flex justify-between mt-0.5">
              <span className={`${MONO_SM}`} style={{ color: `${T.violet}80` }}>Théorique</span>
              <span className={`${MONO_SM} font-bold tabular-nums`} style={{ color: T.violet }}>{theoretical.toFixed(0)}%</span>
            </div>
          </div>

          {/* Réel */}
          <div className="relative">
            <div className="h-1 rounded-full bg-white/5 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${real}%` }}
                transition={{ duration: 1.4, ease: 'easeOut', delay: 0.2 }}
                className="h-full rounded-full"
                style={{ background: T.gold, boxShadow: `0 0 6px ${T.gold}60` }}
              />
            </div>
            <div className="flex justify-between mt-0.5">
              <span className={`${MONO_SM}`} style={{ color: `${T.gold}80` }}>Historique</span>
              <span className={`${MONO_SM} font-bold tabular-nums`} style={{ color: T.gold }}>{real.toFixed(0)}%</span>
            </div>
          </div>
        </div>

        {/* Note contextuelle tronquée */}
        <p className="text-[9px] text-white/25 italic leading-snug line-clamp-2 text-left">
          {historicalNote}
        </p>
      </button>

      {/* Modal explainer */}
      {createPortal(
        <AnimatePresence>
          {showExplainer && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
              style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)' }}
              onClick={() => setShowExplainer(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 10 }}
                animate={{ scale: 1,    y: 0  }}
                exit={{ scale: 0.95, y: 10 }}
                onClick={e => e.stopPropagation()}
                className="max-w-lg w-full rounded-2xl overflow-hidden"
                style={{ background: T.surface0, border: `1px solid ${T.border1}`, boxShadow: '0 0 80px rgba(0,0,0,0.9)' }}
              >
                {/* Header */}
                <div className="px-6 py-5 border-b flex items-start justify-between" style={{ borderColor: T.border0 }}>
                  <div>
                    <h3 className="text-base font-semibold text-white">L'écart théorique / réel</h3>
                    <p className={`${MONO_SM} mt-0.5`} style={{ color: `${T.violet}80` }}>Calibration bayésienne</p>
                  </div>
                  <button onClick={() => setShowExplainer(false)} className="p-1.5 rounded-lg hover:bg-white/5 text-white/30 hover:text-white transition-colors">
                    <X size={15} />
                  </button>
                </div>

                <div className="p-6 space-y-5">
                  {/* Barres agrandies */}
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-2">
                        <div>
                          <p className="text-[11px] font-semibold text-white/80">Probabilité théorique</p>
                          <p className="text-[10px] text-white/35 mt-0.5">Calculée par optimisation génétique dans la simulation</p>
                        </div>
                        <span className="text-2xl font-bold font-mono tabular-nums" style={{ color: T.violet }}>{theoretical.toFixed(0)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${theoretical}%`, background: T.violet }} />
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="h-px flex-1" style={{ background: `${T.red}40` }} />
                      <span className="text-[10px] font-mono font-bold" style={{ color: gapColor }}>ABSORPTION SYSTÉMIQUE : −{gap.toFixed(0)}%</span>
                      <div className="h-px flex-1" style={{ background: `${T.red}40` }} />
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <div>
                          <p className="text-[11px] font-semibold text-white/80">Probabilité historique réelle</p>
                          <p className="text-[10px] text-white/35 mt-0.5">Fréquence observée sur des cas similaires (1900–2025)</p>
                        </div>
                        <span className="text-2xl font-bold font-mono tabular-nums" style={{ color: T.gold }}>{real.toFixed(0)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${real}%`, background: T.gold }} />
                      </div>
                    </div>
                  </div>

                  {/* Explication */}
                  <div className="p-4 rounded-xl space-y-2" style={{ background: T.surface1, border: `1px solid ${T.border0}` }}>
                    <p className="text-[11px] text-white/60 leading-relaxed">
                      La simulation modélise les conditions optimales. Dans le monde réel, les forces de reproduction sociale, les résistances institutionnelles et les contraintes de ressources créent un <span className="text-white/85 font-medium">écart structurel</span> entre ce qui est théoriquement possible et ce qui se réalise.
                    </p>
                    <div className="pt-2 border-t" style={{ borderColor: T.border0 }}>
                      <p className="text-[10px] italic leading-relaxed" style={{ color: `${T.gold}90` }}>
                        "{historicalNote}"
                      </p>
                    </div>
                  </div>

                  <p className={`text-center ${MONO_SM} text-white/15`}>
                    Ce n'est pas une limite de la simulation — c'est ce qu'elle vous enseigne.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// [3] INSIGHT OVERLAY — flash contextuel post-action (non-bloquant)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * useInsightOverlay — hook qui écoute les actions et prépare le contenu du flash.
 * Retourne { insight, dismiss }.
 */
export const useInsightOverlay = () => {
  const { engine, completedActions } = usePesa();
  const [insight, setInsight] = useState(null);
  const prevCountRef = useRef(0);
  const timerRef = useRef(null);

  const completedArr = completedActions instanceof Set ? [...completedActions] : [];

  useEffect(() => {
    if (completedArr.length <= prevCountRef.current) return;
    prevCountRef.current = completedArr.length;

    const lastId = completedArr[completedArr.length - 1];
    if (!lastId) return;

    const action = engine?.case?.steps?.flatMap(s => s.actions || []).find(a => a.id === lastId);
    if (!action) return;

    // Construire le contenu du flash
    const params  = engine?.state?.parameters || {};
    const initial = engine?.state?.initialParameters || {};
    const deltas  = Object.entries(params)
      .map(([k, v]) => ({ key: k, before: initial[k] ?? 50, after: v, delta: v - (initial[k] ?? 50) }))
      .filter(d => Math.abs(d.delta) >= 2)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      .slice(0, 4);

    setInsight({
      id:          lastId,
      label:       action.label,
      whatHappened: action.deepFeedback?.whatHappened,
      whyItMatters: action.deepFeedback?.whyItMatters,
      boostCondition: action.deepFeedback?.boostCondition,
      competency:  action.competencySignal,
      causalChain: (action.causalChain || []).slice(0, 2),
      deltas,
    });

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setInsight(null), 19000);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [completedArr.length]);

  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setInsight(null);
  }, []);

  return { insight, dismiss };
};

/**
 * InsightOverlay — s'affiche dans le coin bas-droit pendant 9s après chaque action.
 * Non-bloquant, dismissable, ne masque pas les actions.
 */
export const InsightOverlay = () => {
  const { insight, dismiss } = useInsightOverlay();
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!insight) { setProgress(100); return; }
    setProgress(100);
    const start = Date.now();
    const duration = 19000;
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.max(0, 100 - (elapsed / duration) * 100));
      if (elapsed >= duration) clearInterval(id);
    }, 80);
    return () => clearInterval(id);
  }, [insight?.id]);

  return createPortal(
    <AnimatePresence>
      {insight && (
        <motion.div
          key={insight.id}
          initial={{ opacity: 0, x: 40, scale: 0.96 }}
          animate={{ opacity: 1,  x: 0,  scale: 1 }}
          exit={{ opacity: 0, x: 40, scale: 0.96 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-8 right-8 z-[500] w-80 rounded-2xl overflow-hidden"
          style={{
            background: T.surface0,
            border: `1px solid ${T.border1}`,
            boxShadow: `0 0 60px rgba(0,0,0,0.8), 0 0 0 1px ${T.border0}`,
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Barre de progression */}
          <div className="h-0.5 bg-white/5">
            <motion.div
              className="h-full"
              style={{
                width: `${progress}%`,
                background: `linear-gradient(90deg, ${T.violet}, ${T.gold})`,
                transition: 'width 0.08s linear',
              }}
            />
          </div>

          <div className="p-5 space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Zap size={10} className="text-amber-400/70" />
                  <span className={`${MONO_SM} text-amber-400/60`}>Action jouée</span>
                </div>
                <p className="text-[13px] font-semibold text-white leading-tight">{insight.label}</p>
              </div>
              <button onClick={dismiss} className="p-1 rounded-lg hover:bg-white/8 text-white/25 hover:text-white/60 transition-colors shrink-0">
                <X size={13} />
              </button>
            </div>

            {/* Compétence */}
            {insight.competency && (
              <div className="flex items-center gap-2">
                <Shield size={9} className="text-white/25 shrink-0" />
                <span className="text-[10px] text-white/50">{insight.competency.name}</span>
                <StatusTag color="amber">{insight.competency.level}</StatusTag>
              </div>
            )}

            {/* Ce qui s'est passé */}
            {insight.whatHappened && (
              <p className="text-[11px] text-white/60 leading-relaxed">
                {insight.whatHappened}
              </p>
            )}

            {/* Deltas paramétriques */}
            {insight.deltas.length > 0 && (
              <div className="grid grid-cols-2 gap-1.5">
                {insight.deltas.map(d => (
                  <div
                    key={d.key}
                    className="flex items-center justify-between px-2.5 py-1.5 rounded-lg"
                    style={{
                      background: d.delta > 0 ? 'rgba(52,211,153,0.05)' : 'rgba(248,113,113,0.05)',
                      border: `1px solid ${d.delta > 0 ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)'}`,
                    }}
                  >
                    <span className={`${MONO_SM} text-white/35 truncate`}>{d.key}</span>
                    <span
                      className={`${MONO_SM} font-bold tabular-nums ml-1`}
                      style={{ color: d.delta > 0 ? T.emerald : T.red }}
                    >
                      {d.delta > 0 ? '+' : ''}{d.delta}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Chaîne causale */}
            {insight.causalChain.length > 0 && (
              <div className="space-y-1">
                {insight.causalChain.map((link, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px]">
                    <span className="text-white/30 truncate max-w-[100px]">{link.from}</span>
                    <ArrowRight size={8} className="text-white/15 shrink-0" />
                    <span className="text-emerald-400/65 truncate">{link.to}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Levier suivant */}
            {insight.boostCondition && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(201,165,90,0.06)', border: `1px solid ${T.goldGlow}` }}>
                <TrendingUp size={9} className="text-amber-400/60 shrink-0 mt-0.5" />
                <p className="text-[10px] text-amber-200/65 italic leading-relaxed">{insight.boostCondition}</p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// [4] DEBRIEF ONBOARDING — 3 slides d'introduction avant RapportEnquete
// ─────────────────────────────────────────────────────────────────────────────

/**
 * DebriefOnboarding — 3 slides qui recontextualisent le rapport avant de l'ouvrir.
 * Props : onComplete (callback → ouvre RapportEnquete), onClose
 */
export const DebriefOnboarding = ({ onComplete, onClose }) => {
  const { engine, currentSolution, completedActions } = usePesa();
  const [step, setStep] = useState(0);

  const completedArr = completedActions instanceof Set ? [...completedActions] : [];

  // Données pour les slides
  const theoretical = useMemo(() => {
    // Utilise la même source que le rapport : l'interprétation
    const interpTheo = currentSolution?.interpretation?.theoreticalPromise;
    if (interpTheo != null) {
      // S'assure que la valeur est entre 0 et 100 (elle l'est déjà normalement)
      return Math.max(0, Math.min(100, interpTheo > 1 ? interpTheo : interpTheo * 100));
    }
    // Fallback sur l’état du moteur si l’interprétation n’existe pas
    const raw = engine?.state?.optimalPath?.probabilityOfSuccess
      ?? engine?.state?.prediction?.probability ?? 0;
    return Math.max(0, Math.min(100, raw > 1 ? raw : raw * 100));
  }, [engine?.state, currentSolution?.interpretation?.theoreticalPromise]);

  const real = useMemo(() => {
    if (currentSolution?.interpretation) {
      const r = currentSolution.interpretation.realWorldProbability
        ?? currentSolution.interpretation.theoreticalPromise ?? 10;
      return Math.max(0, Math.min(100, r > 1 ? r : r * 100));
    }
    return Math.round(theoretical * 0.58);
  }, [currentSolution, theoretical]);

  const gap = theoretical - real;

  const keyActions = useMemo(() => {
    return completedArr.slice(0, 5).map(id => {
      const action = engine?.case?.steps?.flatMap(s => s.actions || []).find(a => a.id === id);
      return action ? { id, label: action.label, competency: action.competencySignal } : null;
    }).filter(Boolean);
  }, [completedArr, engine?.case?.steps]);

  const missedCFs = useMemo(() => {
    return (engine?.counterfactuals || [])
      .sort((a, b) => b.deltaProbability - a.deltaProbability)
      .slice(0, 3);
  }, [engine?.counterfactuals]);

  const slides = [
    {
      id: 'choices',
      icon: CheckCircle2,
      color: T.emerald,
      title: 'Ce que vous avez construit',
      subtitle: `${completedArr.length} décision${completedArr.length > 1 ? 's' : ''} prises`,
      content: (
        <div className="space-y-3">
          <p className="text-[13px] text-white/60 leading-relaxed">
            Voici les choix qui ont le plus façonné la trajectoire de cette simulation.
            Chacun a déclenché des effets en cascade que le rapport détaillera.
          </p>
          <div className="space-y-2">
            {keyActions.length > 0 ? keyActions.map((a, i) => (
              <div key={a.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                style={{ background: T.surface2, border: `1px solid ${T.border0}` }}>
                <span className="text-[10px] font-mono font-bold text-white/25 tabular-nums w-5 shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-[12px] text-white/75 flex-1 truncate">{a.label}</span>
                {a.competency && <StatusTag color="amber">{a.competency.level}</StatusTag>}
              </div>
            )) : (
              <p className={`text-center py-4 ${MONO} text-white/20 italic`}>Aucune action enregistrée.</p>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'gap',
      icon: GitBranch,
      color: T.gold,
      title: 'Pourquoi les chiffres divergent',
      subtitle: `−${gap.toFixed(0)}% d'absorption systémique`,
      content: (
        <div className="space-y-4">
          <p className="text-[13px] text-white/60 leading-relaxed">
            La simulation vous a donné <strong className="text-white/85">{theoretical.toFixed(0)}%</strong> de probabilité théorique.
            Les données historiques réelles indiquent <strong className="text-white/85">{real.toFixed(0)}%</strong>.
            Ce n'est pas un bug — c'est la leçon centrale.
          </p>

          {/* Visualisation de l'écart */}
          <div className="space-y-3 p-4 rounded-xl" style={{ background: T.surface2, border: `1px solid ${T.border0}` }}>
            {[
              { label: 'Probabilité théorique', value: theoretical, color: T.violet },
              { label: 'Probabilité historique réelle', value: real, color: T.gold },
            ].map(item => (
              <div key={item.label}>
                <div className="flex justify-between mb-1.5">
                  <span className="text-[11px] text-white/50">{item.label}</span>
                  <span className="text-[12px] font-mono font-bold tabular-nums" style={{ color: item.color }}>
                    {item.value.toFixed(0)}%
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${item.value}%`, background: item.color }} />
                </div>
              </div>
            ))}
          </div>

          <p className="text-[11px] italic text-white/40 leading-relaxed px-1">
            Les forces de reproduction sociale (Bourdieu), les résistances institutionnelles (Foucault)
            et les jeux d'acteurs (Crozier) absorbent systématiquement une partie du potentiel transformateur.
          </p>
        </div>
      ),
    },
    {
      id: 'unlocked',
      icon: Sparkles,
      color: T.violet,
      title: 'Ce que vous auriez pu débloquer',
      subtitle: missedCFs.length > 0 ? `${missedCFs.length} levier${missedCFs.length > 1 ? 's' : ''} non activé${missedCFs.length > 1 ? 's' : ''}` : 'Trajectoire optimale atteinte',
      content: (
        <div className="space-y-4">
          <p className="text-[13px] text-white/60 leading-relaxed">
            Le rapport complet révèle les chemins alternatifs. Voici un aperçu des leviers
            qui auraient eu le plus d'impact sur la probabilité de transformation.
          </p>

          {missedCFs.length > 0 ? (
            <div className="space-y-2.5">
              {missedCFs.map((cf, i) => (
                <div key={i} className="p-3 rounded-xl" style={{ background: T.surface2, border: `1px solid ${T.border0}` }}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div
                      className="px-2 py-0.5 rounded-md text-[9px] font-mono font-bold"
                      style={{ background: `${T.violet}15`, color: T.violet, border: `1px solid ${T.violet}30` }}
                    >
                      +{(cf.deltaProbability * 100).toFixed(0)}%
                    </div>
                    <span className={`${MONO_SM} text-white/25`}>impact potentiel</span>
                  </div>
                  <p className="text-[11px] text-white/65">{cf.actionTitle}</p>
                  {cf.description && (
                    <p className="text-[10px] text-white/35 mt-1 leading-relaxed italic line-clamp-2">{cf.description}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center py-6 gap-3">
              <CheckCircle2 size={28} className="text-emerald-400" />
              <p className="text-[12px] text-emerald-300/70 text-center">
                Vous avez activé les leviers les plus déterminants.
              </p>
            </div>
          )}

          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
            style={{ background: `${T.violet}08`, border: `1px dashed ${T.violet}30` }}>
            <Lightbulb size={11} style={{ color: `${T.violet}80` }} className="shrink-0" />
            <p className="text-[10px] text-white/45 leading-relaxed italic">
              La simulation est rejouable. Des conditions initiales différentes mènent à des trajectoires différentes.
            </p>
          </div>
        </div>
      ),
    },
  ];

  const current = slides[step];
  const Icon = current.icon;
  const isLast = step === slides.length - 1;

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9000] flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(16px)' }}
    >
      <motion.div
        key={current.id}
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1,  y: 0,  scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.97 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-xl w-full rounded-3xl overflow-hidden flex flex-col"
        style={{
          background: T.surface0,
          border: `1px solid ${T.border1}`,
          boxShadow: '0 0 120px rgba(0,0,0,0.9)',
          maxHeight: '90vh',
        }}
      >
        {/* Progress dots */}
        <div className="flex items-center justify-between px-6 pt-5 pb-0 shrink-0">
          <div className="flex gap-2">
            {slides.map((s, i) => (
              <div
                key={s.id}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === step ? '20px' : '6px',
                  height: '6px',
                  background: i === step ? current.color : T.border1,
                }}
              />
            ))}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-white/25 hover:text-white/60 transition-colors">
            <X size={14} />
          </button>
        </div>

        {/* Header slide */}
        <div className="px-6 py-6 shrink-0">
          <div
            className="w-10 h-10 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: `${current.color}15`, border: `1px solid ${current.color}30` }}
          >
            <Icon size={18} style={{ color: current.color }} />
          </div>
          <div className={`${MONO_SM} mb-2`} style={{ color: `${current.color}70` }}>
            {String(step + 1).padStart(2, '0')} / {slides.length}
          </div>
          <h2 className="text-xl font-semibold text-white/90 leading-tight">{current.title}</h2>
          <p className={`${MONO} mt-1.5`} style={{ color: `${current.color}80` }}>{current.subtitle}</p>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto px-6 pb-4 scrollbar-thin scrollbar-thumb-white/5 scrollbar-track-transparent">
          {current.content}
        </div>

        {/* Footer navigation */}
        <div className="px-6 py-5 flex items-center justify-between shrink-0 border-t" style={{ borderColor: T.border0 }}>
          <button
            onClick={() => step > 0 ? setStep(s => s - 1) : onClose()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-mono text-white/35 hover:text-white/60 transition-colors"
          >
            <ChevronLeft size={13} />
            {step === 0 ? 'Annuler' : 'Précédent'}
          </button>

          <button
            onClick={() => isLast ? onComplete() : setStep(s => s + 1)}
            className="flex items-center gap-2.5 px-5 py-2.5 rounded-xl font-semibold text-[12px] transition-all"
            style={{
              background: isLast
                ? `linear-gradient(135deg, ${T.violet}, ${T.gold})`
                : `${current.color}20`,
              color: isLast ? '#fff' : current.color,
              border: `1px solid ${current.color}40`,
              boxShadow: isLast ? `0 0 20px ${T.violet}40` : 'none',
            }}
          >
            {isLast ? (
              <>
                <FileText size={13} />
                Ouvrir le rapport complet
              </>
            ) : (
              <>
                Suivant
                <ChevronRight size={13} />
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PANNEAUX HÉRITÉS (inchangés, réexportés pour compatibilité)
// ─────────────────────────────────────────────────────────────────────────────

export const DynamicsPanel = () => {
  const { engine } = usePesa();
  const dynamics    = engine?.case?.simulationEngine?.dynamics || [];
  const currentTick = engine?.currentTick || 0;
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setProgress(p => (p + 8) % 100), 600);
    return () => clearInterval(id);
  }, []);

  if (dynamics.length === 0) return null;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between px-3 py-2.5 rounded-lg"
        style={{ background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.1)' }}>
        <div>
          <div className={`${MONO} text-white/25 mb-1`}>Cycle</div>
          <div className="h-1 w-32 rounded-full overflow-hidden bg-black/40">
            <motion.div className="h-full rounded-full"
              style={{ background: 'linear-gradient(90deg, #7c3aed, #f59e0b)', width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
        <span className="text-sm font-mono font-bold text-amber-400 tabular-nums">
          T-{String(currentTick).padStart(3, '0')}
        </span>
      </div>
      {dynamics.slice(0, 4).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {dynamics.slice(0, 4).map((d, i) => (
            <StatusTag key={i} color="purple">{d.type || 'Process'}</StatusTag>
          ))}
        </div>
      )}
    </div>
  );
};

export const PredictionPanel = () => {
  const { engine, phase, currentSolution } = usePesa();
  const [showBayesNotice, setShowBayesNotice] = useState(false);

  const getStatus = (pct) => {
    if (pct >= 90) return { text: 'Émancipation quasi-certaine', color: 'emerald' };
    if (pct >= 75) return { text: 'Transformation probable',     color: 'emerald' };
    if (pct >= 60) return { text: 'Chemin favorable',            color: 'amber'   };
    if (pct >= 40) return { text: 'Issue incertaine',            color: 'purple'  };
    return           { text: 'Reproduction dominante',          color: 'red'     };
  };

  let proba = 0;
  if (phase === 'finished' && currentSolution?.interpretation) {
    proba = currentSolution.interpretation.realWorldProbability ?? currentSolution.interpretation.theoreticalPromise ?? 10;
  } else {
    proba = engine?.state?.optimalPath?.probabilityOfSuccess ?? engine?.state?.prediction?.probability ?? 0;
  }
  const percentage = Math.max(0, Math.min(100, proba > 1 ? proba : proba * 100));
  const status     = getStatus(percentage);
  const barColor   = { emerald: '#10b981', amber: '#f59e0b', red: '#ef4444', purple: '#a78bfa' }[status.color];

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <div className={`${MONO} text-white/25 mb-1`}>Probabilité</div>
          <span className="text-3xl font-bold font-mono text-white tabular-nums">
            {percentage.toFixed(1)}<span className="text-lg text-white/40">%</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <StatusTag color={status.color}>{status.text}</StatusTag>
          <button onClick={() => setShowBayesNotice(true)} className="opacity-25 hover:opacity-70 transition-opacity">
            <HelpCircle size={11} />
          </button>
        </div>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden bg-white/5">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{ background: barColor, boxShadow: `0 0 8px ${barColor}60` }}
        />
      </div>
      <div className={`${MONO} text-white/15`}>
        {phase === 'finished' ? 'Données historiques réelles' : 'Prédiction optimisation génétique'}
      </div>
      {createPortal(
        <AnimatePresence>
          {showBayesNotice && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-black/85 backdrop-blur-sm"
              onClick={() => setShowBayesNotice(false)}>
              <motion.div initial={{ scale: 0.96, y: 8 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 8 }}
                onClick={e => e.stopPropagation()}
                className="max-w-md w-full rounded-2xl p-7 shadow-2xl"
                style={{ background: '#0d0d14', border: '1px solid rgba(167,139,250,0.2)' }}>
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-base font-semibold text-violet-300">Calibration bayésienne</h3>
                    <div className={`${MONO} text-white/25 mt-0.5`}>Bifurcation Engine</div>
                  </div>
                  <button onClick={() => setShowBayesNotice(false)} className="p-1.5 rounded-lg hover:bg-white/5 text-white/30 hover:text-white transition-colors">
                    <X size={16} />
                  </button>
                </div>
                <div className="space-y-3 text-[12px] leading-relaxed text-white/60">
                  <p>Ce pourcentage n'est pas une prophétie. C'est la <span className="text-white/90 font-medium">fréquence historique réelle</span> (1900–2025) de trajectoires similaires.</p>
                  <div className="px-4 py-3 rounded-lg text-[11px] italic text-violet-300/80"
                    style={{ background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.1)' }}>
                    Exemple : Une école émancipatrice réussit dans la simulation à 95 %… mais dans le monde réel, 88 % ont été écrasées → probabilité réelle ≈ 12 %.
                  </div>
                  <p className={`${MONO} text-white/20`}>
                    {phase === 'finished' ? 'Cas historiques réels similaires.' : 'Prédiction théorique — optimisation génétique.'}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export const HistoryPanel = () => {
  const { history } = usePesa();
  return (
    <div className="flex flex-col max-h-[260px]">
      <div className="flex-1 overflow-y-auto px-3 py-2.5 space-y-1 scrollbar-thin scrollbar-thumb-white/5 scrollbar-track-transparent">
        {history.slice().reverse().map((h, i) => (
          <div key={i} className="flex items-start gap-2.5 px-2.5 py-2 rounded-lg hover:bg-white/[0.03] transition-colors group">
            <div className="shrink-0 w-0.5 self-stretch rounded-full mt-0.5" style={{ background: 'rgba(245,158,11,0.3)' }} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <span className={`${MONO} text-amber-500/50`}>{new Date().toLocaleTimeString().slice(3, 8)}</span>
                <span className={`${MONO} text-white/60 font-semibold`}>{h.type}</span>
              </div>
              <p className={`${MONO} text-white/25 italic truncate`}>{h.actionId || 'Internal_Shift'}</p>
            </div>
          </div>
        ))}
        {history.length === 0 && (
          <div className="text-center py-8"><span className={`${MONO} text-white/15`}>Aucun événement</span></div>
        )}
      </div>
    </div>
  );
};

export const SolutionsPanel = () => {
  const { currentSolution, phase } = usePesa();
  const isPlaying = phase === 'playing' || phase === 'loading';
  return (
    <div className="p-4 space-y-3">
      {isPlaying ? (
        <div className="space-y-2.5">
          <div className={`${MONO} text-white/20`}>Calcul de convergence</div>
          <div className="flex gap-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-0.5 flex-1 rounded-full bg-amber-500/20 animate-pulse" style={{ animationDelay: `${i * 100}ms` }} />
            ))}
          </div>
        </div>
      ) : currentSolution ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />
            <span className="text-sm font-semibold text-white leading-tight">{currentSolution.name}</span>
            <StatusTag color="emerald">RÉSOLU</StatusTag>
          </div>
          <p className="text-[11px] leading-relaxed text-white/40 line-clamp-2">{currentSolution.description}</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div className={`${MONO} text-white/25 mb-0.5`}>Complexité</div>
              <div className="text-[11px] font-mono text-white/70">{currentSolution.difficulty}</div>
            </div>
            <div className="px-3 py-2 rounded-lg" style={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.1)' }}>
              <div className={`${MONO} text-white/25 mb-0.5`}>Stabilité</div>
              <div className="text-[11px] font-mono text-emerald-400">94%</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg"
          style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.1)' }}>
          <Activity size={11} className="animate-pulse text-red-400 shrink-0" />
          <span className={`${MONO} text-red-400`}>Équilibre non atteint</span>
        </div>
      )}
    </div>
  );
};

export const CounterfactualsPanel = ({ counterfactuals = [] }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  if (!counterfactuals?.length) return null;
  const top3 = [...counterfactuals].sort((a, b) => b.deltaProbability - a.deltaProbability).slice(0, 3);
  return (
    <div className="p-4 space-y-3">
      <div className="space-y-2">
        {top3.map((cf, i) => (
          <div key={i} className="flex items-center gap-3 group">
            <div className="h-6 rounded shrink-0 flex items-center px-2"
              style={{ width: `${Math.max(28, cf.deltaProbability * 100 * 0.6)}px`, background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.15)', transition: 'width 0.3s ease' }}>
              <span className="text-[9px] font-mono font-bold text-violet-400 whitespace-nowrap">+{(cf.deltaProbability * 100).toFixed(0)}%</span>
            </div>
            <span className="text-[11px] text-white/50 truncate group-hover:text-white/75 transition-colors">{cf.actionTitle}</span>
          </div>
        ))}
      </div>
      <button onClick={() => setIsModalOpen(true)}
        className="w-full py-2 rounded-lg text-[9px] font-mono tracking-widest uppercase border border-violet-500/15 text-violet-400/50 hover:bg-violet-500/8 hover:text-violet-300 hover:border-violet-500/25 transition-all duration-150">
        Analyse complète →
      </button>
      {createPortal(
        <AnimatePresence>
          {isModalOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
              onClick={() => setIsModalOpen(false)}>
              <motion.div initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 12 }}
                onClick={e => e.stopPropagation()}
                className="max-w-2xl w-full max-h-[85vh] overflow-hidden rounded-2xl flex flex-col"
                style={{ background: '#0d0d14', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 0 60px rgba(0,0,0,0.8)' }}>
                <div className="px-6 py-5 border-b border-white/5 flex justify-between items-start shrink-0">
                  <div>
                    <h2 className="text-base font-semibold text-white">Analyse des Futurs</h2>
                    <div className={`${MONO} text-violet-400/50 mt-0.5`}>Bifurcation Engine v1.0</div>
                  </div>
                  <button onClick={() => setIsModalOpen(false)} className="p-1.5 hover:bg-white/5 rounded-lg transition-colors text-white/30 hover:text-white">
                    <X size={16} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {counterfactuals.map((cf, i) => (
                    <div key={i} className="p-5 rounded-xl transition-colors" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="flex items-center gap-3 mb-3"><StatusTag color="amber">+{(cf.deltaProbability * 100).toFixed(1)}% impact</StatusTag></div>
                      <h4 className="text-sm font-medium text-white/90 mb-1.5">{cf.actionTitle}</h4>
                      <p className="text-[12px] text-white/55 leading-relaxed">{cf.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

const CausalContent = ({ isModal = false }) => {
  const [activeTab, setActiveTab] = useState('causal');
  const { engine, completedActions } = usePesa();
  const completedArr  = completedActions instanceof Set ? [...completedActions] : [];
  const lastActionId  = completedArr[completedArr.length - 1] || null;
  const lastAction = useMemo(() => {
    if (!lastActionId) return null;
    return engine?.case?.steps?.flatMap(s => s.actions || []).find(a => a.id === lastActionId) || null;
  }, [lastActionId, engine?.case?.steps]);
  const currentStep   = engine?.getCurrentStep?.();
  const globalCausal  = engine?.case?.simulationEngine?.causality || [];
  const stepCausal    = currentStep?.causality || [];
  const causalities   = [...stepCausal, ...globalCausal].slice(0, 6);
  const df   = lastAction?.deepFeedback;
  const cc   = lastAction?.causalChain   || [];
  const comp = lastAction?.competencySignal;
  const hasFeedback = df && (df.whatHappened || df.whyItMatters || df.whatItCosts);
  const tabs = [
    { id: 'causal',     label: 'Causal',     icon: GitBranch  },
    { id: 'feedback',   label: 'Analyse',    icon: Zap,        disabled: !hasFeedback },
    { id: 'competency', label: 'Compétence', icon: Target,     disabled: !comp },
  ];
  return (
    <div className="space-y-3 p-4">
      {lastAction && (
        <div className="flex items-center gap-2">
          <div className="w-1 h-1 rounded-full bg-amber-500/60" />
          <span className={`${MONO} text-amber-500/50 truncate`} title={lastAction.label}>↳ {lastAction.id}</span>
        </div>
      )}
      <div className="flex gap-1">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => !tab.disabled && setActiveTab(tab.id)} disabled={tab.disabled}
            className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[9px] font-mono tracking-wider uppercase transition-all border ${tab.disabled ? 'opacity-20 cursor-not-allowed border-white/5 text-white/20' : activeTab === tab.id ? 'bg-violet-600/15 border-violet-500/25 text-violet-300' : 'bg-transparent border-white/5 text-white/30 hover:text-white/55 hover:border-white/10'}`}>
            <tab.icon size={9} />{tab.label}
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        {activeTab === 'causal' && (
          <motion.div key="causal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-1.5">
            {causalities.length > 0 ? causalities.map((c, i) => (
              <div key={i} className="px-3 py-2.5 rounded-lg transition-colors" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex items-center gap-2 text-[10px] mb-1.5">
                  <span className="text-white/40 truncate flex-1">{c.cause}</span>
                  <ArrowRight size={8} className="text-white/15 shrink-0" />
                  <span className={`font-medium flex-1 text-right truncate ${(c.weight || 0) > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{c.effect}</span>
                </div>
                <div className="h-0.5 w-full bg-white/5 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${(c.weight || 0) > 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.abs((c.weight || 0) * 100)}%`, opacity: 0.7 }} />
                </div>
                {c.evidence?.source && <p className={`${MONO} text-white/15 mt-1 truncate`}>src: {c.evidence.source}</p>}
              </div>
            )) : <p className={`text-center py-5 ${MONO} text-white/15 italic`}>Aucune causalité active</p>}
            {cc.length > 0 && (
              <div className="mt-2 pt-2 border-t border-white/5 space-y-1.5">
                <div className={`${MONO} text-white/20 mb-1.5`}>Chaîne causale</div>
                {cc.map((link, i) => (
                  <div key={i} className="flex items-start gap-2 px-2.5 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.015)' }}>
                    <Crosshair size={8} className="text-amber-400/30 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-[10px] text-white/50">{link.from} → {link.to}</p>
                      {link.mechanism && <p className="text-[10px] text-white/25 italic mt-0.5">{link.mechanism}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
        {activeTab === 'feedback' && hasFeedback && (
          <motion.div key="feedback" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2">
            <div className="px-3 py-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.3)' }}>
              <div className={`${MONO} text-white/20 mb-0.5`}>Dernière action</div>
              <p className="text-[11px] font-medium text-amber-400/80">{lastAction?.label}</p>
            </div>
            {[
              { key: 'whatHappened', label: "Ce qui s'est passé", icon: Activity,    color: 'text-white/70',     border: 'rgba(255,255,255,0.05)' },
              { key: 'whyItMatters', label: 'Pourquoi ça compte',  icon: BookOpen,   color: 'text-amber-300/80', border: 'rgba(245,158,11,0.1)'   },
              { key: 'whatItCosts',  label: 'Ce que ça coûte',     icon: TrendingDown,color: 'text-red-400/70',  border: 'rgba(239,68,68,0.1)'   },
            ].filter(({ key }) => df?.[key]).map(({ key, label, icon: Icon, color, border }) => (
              <div key={key} className="p-3 rounded-lg space-y-1.5" style={{ border: `1px solid ${border}`, background: 'rgba(255,255,255,0.015)' }}>
                <div className="flex items-center gap-1.5">
                  <Icon size={9} className={color} />
                  <span className={`${MONO} text-white/20`}>{label}</span>
                </div>
                <p className={`text-[11px] leading-relaxed ${color}`}>{df[key]}</p>
              </div>
            ))}
            {df?.boostCondition && (
              <div className="p-3 rounded-lg" style={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.12)' }}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <TrendingUp size={9} className="text-emerald-400" />
                  <span className={`${MONO} text-emerald-400/50`}>Levier suivant</span>
                </div>
                <p className="text-[11px] text-emerald-300/70 italic leading-relaxed">{df.boostCondition}</p>
              </div>
            )}
          </motion.div>
        )}
        {activeTab === 'competency' && comp && (
          <motion.div key="competency" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2.5">
            <div className={`p-4 rounded-xl border ${LEVEL_COLORS_CAUSAL[comp.level] || LEVEL_COLORS_CAUSAL.intermédiaire}`}>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <p className="text-sm font-semibold text-white/90">{comp.name}</p>
                <StatusTag color={comp.level === 'expert' ? 'purple' : comp.level === 'avancé' ? 'amber' : 'emerald'}>{comp.level}</StatusTag>
              </div>
              <p className="text-[11px] text-white/50 leading-relaxed">{comp.descriptor}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const CausalPanel = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <>
      <CausalContent />
      <div className="px-4 pb-4">
        <button onClick={() => setIsModalOpen(true)}
          className="w-full py-2 rounded-lg text-[9px] font-mono tracking-widest uppercase border border-white/5 text-white/20 hover:bg-white/[0.03] hover:text-white/40 hover:border-white/8 transition-all duration-150">
          Vue étendue →
        </button>
      </div>
      {createPortal(
        <AnimatePresence>
          {isModalOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
              onClick={() => setIsModalOpen(false)}>
              <motion.div initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 12 }}
                onClick={e => e.stopPropagation()}
                className="max-w-2xl w-full max-h-[85vh] overflow-hidden rounded-2xl flex flex-col"
                style={{ background: '#0d0d14', border: '1px solid rgba(167,139,250,0.15)', boxShadow: '0 0 60px rgba(0,0,0,0.9)' }}>
                <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between shrink-0">
                  <div>
                    <h2 className="text-base font-semibold text-white">Graphe Causal</h2>
                    <div className={`${MONO} text-violet-400/40 mt-0.5`}>Vue complète</div>
                  </div>
                  <button onClick={() => setIsModalOpen(false)} className="p-1.5 hover:bg-white/5 rounded-lg text-white/30 hover:text-white transition-colors">
                    <X size={16} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto"><CausalContent isModal /></div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export const TheoryPanelSide = ({ scenario }) => {
  const params = useParams();
  const { engine } = usePesa();
  const activeId = params.slug || scenario?.slug || scenario?.id || engine?.case?.slug || engine?.case?.id;
  const openLiveTheory = () => {
    if (activeId) window.open(`/theory/generic/${activeId}`, '_blank', 'noopener,noreferrer');
  };
  return (
    <div className="p-4 space-y-3">
      {scenario?.theory?.mainConcepts?.slice(0, 3).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {scenario.theory.mainConcepts.slice(0, 3).map((concept, i) => (
            <StatusTag key={i} color="amber"># {concept}</StatusTag>
          ))}
        </div>
      )}
      <button onClick={openLiveTheory}
        className="w-full py-2.5 rounded-lg flex items-center justify-center gap-2 text-[9px] font-mono tracking-widest uppercase border border-amber-500/15 text-amber-400/50 hover:bg-amber-500/8 hover:text-amber-300 hover:border-amber-500/25 transition-all duration-150">
        <Maximize2 size={11} />Mode Conférence
      </button>
    </div>
  );
};

export const RadialParameters = () => {
  const { darkMode } = useTheme();
  
  const { currentSolution, phase, currentCase, parameters, initialParameters } = usePesa();
  const displayValues = useMemo(() => {
    if (Object.keys(parameters).length > 0 && Object.values(parameters).every(v => typeof v === 'number' && !isNaN(v))) return { ...parameters };
    return { ...initialParameters };
  }, [parameters, initialParameters]);
  const { keys, values, labels } = useMemo(() => {
    const formatLabel = (k) => k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, s => s.toUpperCase()).trim();
    const extractedKeys = Object.keys(displayValues).filter(k => typeof displayValues[k] === 'number' && !isNaN(displayValues[k]));
    return { keys: extractedKeys, values: extractedKeys.map(k => displayValues[k] ?? 50), labels: extractedKeys.map(formatLabel) };
  }, [displayValues]);
  const deltaInfo = useMemo(() => {
    if (!currentSolution?.expectedMetrics || phase !== 'finished') return null;
    const expected = currentSolution.expectedMetrics;
    const valid = keys.filter(k => expected[k] !== undefined);
    if (valid.length === 0) return null;
    const avgE = Math.round(valid.reduce((s, k) => s + (expected[k] || 0), 0) / valid.length);
    const avgC = Math.round(valid.reduce((s, k) => s + (displayValues[k] || 50), 0) / valid.length);
    return { delta: avgC - avgE, avgExpected: avgE, avgCurrent: avgC };
  }, [currentSolution, phase, keys, displayValues]);
  const status = !deltaInfo
    ? { label: 'ACQUISITION',    color: '#a78bfa' }
    : deltaInfo.delta >= 10
    ? { label: 'SURPERFORMANCE', color: '#34d399' }
    : deltaInfo.delta < 0
    ? { label: 'ÉROSION',        color: '#f87171' }
    : { label: 'COHÉRENCE',      color: '#a78bfa' };
  const radarData = useMemo(() => ({
    labels,
    datasets: [{
      label: 'Spectre Actuel',
      data: values,
      backgroundColor: `${status.color}08`,
      borderColor: status.color,
      borderWidth: 1.5,
      pointBackgroundColor: status.color,
      pointBorderColor: 'rgba(255,255,255,0.12)',
      pointBorderWidth: 1,
      pointRadius: 3,
      pointHoverRadius: 5,
      tension: 0.3,
    }],
  }), [labels, values, status.color]);
  const radarOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    animation: { duration: 500, easing: 'easeOutQuart' },
    plugins: { legend: { display: false }, tooltip: { enabled: true, backgroundColor: 'rgba(8,8,14,0.97)', titleFont: { family: '"DM Mono", monospace', size: 10 }, bodyFont: { family: '"DM Mono", monospace', size: 10 }, padding: 10, borderColor: 'rgba(167,139,250,0.15)', borderWidth: 1, callbacks: { label: (ctx) => ` ${ctx.parsed.r}%` } } },
    scales: { r: { angleLines: { color: 'rgba(255,255,255,0.03)' }, grid: { color: 'rgba(255,255,255,0.05)', lineWidth: 0.5 }, pointLabels: { color: 'rgba(255,255,255,0.4)', font: { size: 9, family: '"DM Mono", monospace', weight: '400' }, padding: 8 }, ticks: { display: false, stepSize: 25 }, min: 0, max: 100, beginAtZero: true } },
  }), [darkMode]);
  if (values.length === 0) return (
    <div className="rounded-xl p-6 text-center" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.05)' }}>
      <span className="text-[9px] font-mono text-white/20 tracking-widest uppercase">Aucune métrique détectée</span>
    </div>
  );
  return (
    <div className="relative rounded-xl overflow-hidden" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)' }}>
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: `linear-gradient(rgba(255,255,255,0.012) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,0.012) 1px, transparent 1px)`, backgroundSize: '32px 32px' }} />
      <div className="absolute top-0 left-0 w-5 h-5 border-t border-l rounded-tl-xl" style={{ borderColor: 'rgba(167,139,250,0.18)' }} />
      <div className="absolute top-0 right-0 w-5 h-5 border-t border-r rounded-tr-xl" style={{ borderColor: 'rgba(167,139,250,0.18)' }} />
      <div className="absolute bottom-0 left-0 w-5 h-5 border-b border-l rounded-bl-xl" style={{ borderColor: 'rgba(167,139,250,0.07)' }} />
      <div className="absolute bottom-0 right-0 w-5 h-5 border-b border-r rounded-br-xl" style={{ borderColor: 'rgba(167,139,250,0.07)' }} />
      <div className="relative z-10 p-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Activity size={10} style={{ color: 'rgba(167,139,250,0.55)' }} />
              <span className="text-[9px] font-mono tracking-[0.22em] uppercase text-white/25">Métriques Système</span>
            </div>
            <h2 className="text-sm font-semibold text-white/80 tracking-tight">Analyse Spectrale</h2>
          </div>
          <div className="px-2 py-1 rounded-lg text-[9px] font-mono tracking-widest" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.05)', color: 'rgba(167,139,250,0.55)' }}>
            {currentCase?.slug?.toUpperCase() || '—'}
          </div>
        </div>
        <div className="flex justify-center mb-4">
          <div className="w-full max-w-[280px]"><Radar data={radarData} options={radarOptions} /></div>
        </div>
        <div className="flex items-center justify-between px-3 py-2.5 rounded-lg mb-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0" style={{ background: status.color, boxShadow: `0 0 6px ${status.color}` }} />
            <div>
              <div className="text-[8px] font-mono text-white/20 uppercase tracking-widest mb-0.5">Statut</div>
              <div className="text-[10px] font-mono font-semibold" style={{ color: status.color }}>{status.label}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[8px] font-mono text-white/20 uppercase tracking-widest mb-0.5">Variance</div>
            <div className="text-lg font-bold font-mono tabular-nums" style={{ color: deltaInfo?.delta < 0 ? '#f87171' : '#34d399' }}>
              {deltaInfo ? `${deltaInfo.delta > 0 ? '+' : ''}${deltaInfo.delta}%` : '—'}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {keys.slice(0, 8).map((k, i) => {
            const val = displayValues[k] ?? 0;
            const pct = Math.min(val, 100);
            const initialVal = initialParameters?.[k];
            const delta = initialVal !== undefined ? val - initialVal : null;
            const deltaAbs = delta !== null ? Math.abs(Math.round(delta)) : null;
            const deltaColor = delta === null ? '' : delta > 0 ? '#34d399' : delta < 0 ? '#f87171' : 'rgba(255,255,255,0.4)';
            const deltaSymbol = delta === null ? '' : delta > 0 ? '↑' : delta < 0 ? '↓' : '•';
            return (
              <div key={k} className="relative overflow-hidden rounded-lg" style={{ background: 'rgba(10,10,18,0.7)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(4px)' }}>
                <div className="absolute inset-y-0 left-0 rounded-l-lg" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${status.color}30, ${status.color}18)`, transition: 'width 0.7s cubic-bezier(0.2, 0.9, 0.4, 1.1)', boxShadow: `0 0 8px ${status.color}20` }} />
                <div className="relative flex items-center justify-between gap-2 px-3 py-2.5">
                  <div className="flex items-center gap-1.5 overflow-hidden">
                    <span className="text-[9px] font-mono font-medium uppercase tracking-wide truncate" style={{ color: 'rgba(255,255,255,0.65)', textShadow: '0 0 4px rgba(0,0,0,0.5)' }}>{labels[i]}</span>
                    {delta !== null && <span className="text-[8px] font-mono tabular-nums font-medium" style={{ color: deltaColor, textShadow: '0 0 2px rgba(0,0,0,0.3)' }}>{deltaSymbol} {deltaAbs}%</span>}
                  </div>
                  <span className="text-[12px] font-mono font-bold tabular-nums" style={{ color: '#ffffffcc', textShadow: '0 0 6px rgba(0,0,0,0.3)' }}>{val}%</span>
                </div>
              </div>
            );
          })}
        </div>
        {currentSolution && phase === 'finished' && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut' }}
            className="mt-3 flex items-center gap-2.5 px-3 py-2.5 rounded-lg"
            style={{ background: 'rgba(245,158,11,0.05)', border: '1px dashed rgba(245,158,11,0.18)' }}>
            <Target size={11} style={{ color: 'rgba(245,158,11,0.65)' }} className="shrink-0" />
            <div>
              <span className="text-[8px] font-mono text-white/25 uppercase tracking-widest block mb-0.5">Cible</span>
              <span className="text-[10px] font-mono font-semibold uppercase" style={{ color: 'rgba(245,158,11,0.8)' }}>{currentSolution.name}</span>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export const PESAField = ({ externalState = null, isFullView = false, onNodeClick }) => {
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const { darkMode } = useTheme();
  const { engine: localEngine, theoryMode, getProcessMapPro, currentPhase } = usePesa();
  const [hoveredNode, setHoveredNode]     = useState(null);
  const [hoveredFamily, setHoveredFamily] = useState(null);
  const activeState = useMemo(() => externalState || localEngine?.state || {}, [externalState, localEngine?.state]);
  const { families = [], agents = [], interactions = [] } = activeState;
  const visualMode   = currentPhase?.visualMode   || 'network-pulse';
  const ambiance     = currentPhase?.ambiance      || 'default';
  const tensionLevel = localEngine?.case?.steps?.[activeState.currentStepIndex]?.narrativeArc?.tensionLevel ?? 2;
  const palette    = AMBIANCE_PALETTE[ambiance] || AMBIANCE_PALETTE.default;
  const tension    = TENSION_CONFIG[tensionLevel] || TENSION_CONFIG[2];
  const isFrozen   = visualMode === 'network-freeze';
  const isChaotic  = visualMode === 'network-chaos';
  const liveFamilies = useMemo(() => families.filter(f => f.isActive), [families]);
  const neurons = useMemo(() => {
    const count = liveFamilies.length || 1;
    return liveFamilies.map((f, i) => {
      const angle  = (i / count) * Math.PI * 2 - Math.PI / 2;
      const radius = isFullView ? 0.38 : 0.33;
      const familyAgents = agents.filter(a => a.family === f.name);
      const avgBienEtre  = familyAgents.length
        ? familyAgents.reduce((s, a) => s + (a.resources instanceof Map ? a.resources.get('bienEtre') : (a.resources?.bienEtre ?? 50)), 0) / familyAgents.length
        : 50;
      return { id: f.name, name: f.name, narrativeRole: f.narrativeRole || null, isToxic: f.isToxic || false, agents: familyAgents, baseX: Math.cos(angle) * radius, baseY: Math.sin(angle) * radius, color: FAMILY_COLORS[f.name] || '#94a3b8', vitality: avgBienEtre / 100 };
    });
  }, [liveFamilies, agents, isFullView]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId, time = 0;
    const drawCrosshair = (x, y, size, color, alpha) => {
      ctx.save(); ctx.globalAlpha = alpha; ctx.strokeStyle = color; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(x - size, y); ctx.lineTo(x + size, y); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x, y - size); ctx.lineTo(x, y + size); ctx.stroke();
      ctx.globalAlpha = alpha * 0.5; ctx.lineWidth = 0.5;
      [0.3, 0.6].forEach(t => {
        ctx.beginPath(); ctx.moveTo(x - size * t, y - 3); ctx.lineTo(x - size * t, y + 3); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x + size * t, y - 3); ctx.lineTo(x + size * t, y + 3); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x - 3, y - size * t); ctx.lineTo(x + 3, y - size * t); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(x - 3, y + size * t); ctx.lineTo(x + 3, y + size * t); ctx.stroke();
      }); ctx.restore();
    };
    const drawTechLabel = (x, y, text, color, size = 9, align = 'center') => {
      ctx.save(); ctx.font = `${size}px 'DM Mono', 'Courier New', monospace`; ctx.fillStyle = color; ctx.textAlign = align; ctx.shadowBlur = 6; ctx.shadowColor = 'rgba(0,0,0,0.95)'; ctx.fillText(text, x, y); ctx.shadowBlur = 0; ctx.restore();
    };
    const drawScanLine = (w, h, t) => {
      const y = ((t * 55) % h); const grad = ctx.createLinearGradient(0, y - 6, 0, y + 6);
      grad.addColorStop(0, 'transparent'); grad.addColorStop(0.5, 'rgba(255,255,255,0.012)'); grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad; ctx.fillRect(0, y - 6, w, 12);
    };
    const render = () => {
      if (!isFrozen) time += isChaotic ? 0.018 : 0.007;
      const dpr = window.devicePixelRatio || 1; const w = canvas.width / dpr; const h = canvas.height / dpr;
      const cx = w / 2; const cy = h / 2;
      ctx.clearRect(0, 0, w, h);
      const bgGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.75);
      bgGrad.addColorStop(0, palette.bg); bgGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = bgGrad; ctx.fillRect(0, 0, w, h);
      if (!isFrozen) drawScanLine(w, h, time);
      ctx.save(); ctx.globalAlpha = 0.018; ctx.strokeStyle = darkMode ? '#fff' : '#000'; ctx.lineWidth = 0.4;
      const step = 40;
      for (let x = 0; x < w; x += step) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
      for (let y = 0; y < h; y += step) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
      ctx.restore();
      drawCrosshair(cx, cy, Math.min(w, h) * 0.07, palette.particle, 0.07);
      [0.27, 0.41, 0.54].forEach((r, i) => {
        const radius = Math.min(w, h) * r;
        ctx.beginPath(); ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = darkMode ? `rgba(255,255,255,${0.018 - i * 0.004})` : `rgba(0,0,0,${0.014 - i * 0.003})`;
        ctx.lineWidth = 0.5; ctx.setLineDash([3, 9]); ctx.stroke(); ctx.setLineDash([]);
        if (!isFrozen) {
          const ang = -Math.PI / 2 + time * 0.02 * (i + 1) * 0.3;
          const lx = cx + Math.cos(ang) * radius; const ly = cy + Math.sin(ang) * radius;
          ctx.save(); ctx.globalAlpha = 0.12; ctx.fillStyle = palette.particle;
          ctx.beginPath(); ctx.arc(lx, ly, 1.5, 0, Math.PI * 2); ctx.fill(); ctx.restore();
        }
      });
      const nodesWithPos = neurons.map(n => {
        const jx = isChaotic ? (Math.sin(time * 0.7 + n.baseX * 10) * tension.chaos * w) : 0;
        const jy = isChaotic ? (Math.cos(time * 0.9 + n.baseY * 10) * tension.chaos * h) : 0;
        return { ...n, x: cx + n.baseX * w + jx, y: cy + n.baseY * h + jy };
      });
      if (theoryMode) {
        const proMap = getProcessMapPro?.() || { edges: [] };
        proMap.edges.forEach(edge => {
          const from = nodesWithPos.find(n => n.id === edge.from); const to = nodesWithPos.find(n => n.id === edge.to);
          if (!from || !to) return;
          ctx.beginPath(); ctx.moveTo(from.x, from.y);
          const cpx = cx + Math.sin(time * 0.4) * 20; const cpy = cy + Math.cos(time * 0.4) * 20;
          ctx.quadraticCurveTo(cpx, cpy, to.x, to.y);
          ctx.strokeStyle = edge.weight < 0 ? `rgba(239,68,68,${tension.linkOpacity * 1.5})` : `rgba(16,185,129,${tension.linkOpacity * 1.5})`;
          ctx.lineWidth = 1 + Math.abs(edge.weight || 0) * 1.5; ctx.stroke();
          for (let p = 0; p < 2; p++) {
            const t2 = ((time * 0.02 * (1 + Math.abs(edge.weight || 0))) + p * 0.5) % 1;
            const px = from.x + (to.x - from.x) * t2; const py = from.y + (to.y - from.y) * t2;
            ctx.beginPath(); ctx.arc(px, py, 1.5, 0, Math.PI * 2);
            ctx.fillStyle = edge.weight < 0 ? '#ef4444' : '#10b981';
            ctx.shadowBlur = 6; ctx.shadowColor = ctx.fillStyle; ctx.fill(); ctx.shadowBlur = 0;
          }
        });
      }
      interactions.forEach(link => {
        const from = nodesWithPos.find(n => n.id === link.source); const to = nodesWithPos.find(n => n.id === link.target);
        if (!from || !to) return;
        const strength = link.strength || 0.5;
        ctx.beginPath(); ctx.moveTo(from.x, from.y);
        const cpx = cx + Math.sin(time * 0.3) * (isChaotic ? 30 : 10); const cpy = cy + Math.cos(time * 0.3) * (isChaotic ? 30 : 10);
        ctx.quadraticCurveTo(cpx, cpy, to.x, to.y); ctx.strokeStyle = palette.link; ctx.lineWidth = strength * 2; ctx.stroke();
        if (!isFrozen && tension.chaos > 0.03) {
          const t2 = (time * 0.015) % 1; const px = from.x + (to.x - from.x) * t2; const py = from.y + (to.y - from.y) * t2;
          ctx.beginPath(); ctx.arc(px, py, 1, 0, Math.PI * 2); ctx.fillStyle = palette.particle; ctx.shadowBlur = 4; ctx.shadowColor = palette.particle; ctx.fill(); ctx.shadowBlur = 0;
        }
      });
      nodesWithPos.forEach(n => {
        const isHovered = n.id === hoveredNode; const pulse = isFrozen ? 0 : Math.sin(time * (isChaotic ? 4 : 2) + n.baseX * 5) * 1.5;
        const jitter = isChaotic ? (Math.random() - 0.5) * tension.nodeJitter : 0; const baseSize = isFullView ? 20 : 18;
        const size = baseSize + (1 - n.vitality) * 6 + pulse + jitter;
        if (n.isToxic) {
          ctx.save(); ctx.globalAlpha = 0.05 + Math.sin(time * 3) * 0.025;
          const toxGrad = ctx.createRadialGradient(n.x, n.y, size, n.x, n.y, size * 3.2);
          toxGrad.addColorStop(0, '#ef4444'); toxGrad.addColorStop(1, 'transparent');
          ctx.fillStyle = toxGrad; ctx.beginPath(); ctx.arc(n.x, n.y, size * 3.2, 0, Math.PI * 2); ctx.fill(); ctx.restore();
        }
        const auraSize = size * (1.9 + n.vitality * 0.7);
        const auraGrad = ctx.createRadialGradient(n.x, n.y, size * 0.4, n.x, n.y, auraSize);
        auraGrad.addColorStop(0, `${n.color}`); auraGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = auraGrad; ctx.beginPath(); ctx.arc(n.x, n.y, auraSize, 0, Math.PI * 2); ctx.fill();
        const nodeGrad = ctx.createRadialGradient(n.x - size * 0.3, n.y - size * 0.3, 0, n.x, n.y, size);
        nodeGrad.addColorStop(0, `${n.color}`); nodeGrad.addColorStop(0.6, `${n.color}`); nodeGrad.addColorStop(1, `${n.color}`);
        ctx.beginPath(); ctx.arc(n.x, n.y, size, 0, Math.PI * 2); ctx.fillStyle = nodeGrad; ctx.shadowBlur = isHovered ? 24 : 10; ctx.shadowColor = n.color; ctx.fill(); ctx.shadowBlur = 0;
        ctx.strokeStyle = isHovered ? 'rgba(255,255,255,0.9)' : n.isToxic ? 'rgba(239,68,68,0.55)' : 'rgba(255,255,255,0.2)';
        ctx.lineWidth = isHovered ? 1.5 : 0.75; ctx.stroke();
        if (isHovered) {
          ctx.beginPath(); ctx.arc(n.x, n.y, size * 0.55, 0, Math.PI * 2); ctx.strokeStyle = 'rgba(255,255,255,0.15)'; ctx.lineWidth = 0.5; ctx.stroke();
          drawCrosshair(n.x, n.y, size * 2.2, n.color, 0.35);
        }
        const labelY = n.y - size - 12;
        drawTechLabel(n.x, labelY, n.name.toUpperCase(), darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', 9);
        if (n.isToxic) drawTechLabel(n.x, labelY - 11, '⚠ DOMINANT', 'rgba(239,68,68,0.65)', 8);
        const barW = size * 2.2; const barH = 2; const barX = n.x - barW / 2; const barY = n.y + size + 7;
        ctx.fillStyle = 'rgba(255,255,255,0.06)'; ctx.fillRect(barX, barY, barW, barH);
        const vColor = n.vitality > 0.6 ? '#10b981' : n.vitality > 0.35 ? '#f59e0b' : '#ef4444';
        ctx.fillStyle = vColor; ctx.fillRect(barX, barY, barW * n.vitality, barH);
        if (isFullView || isHovered) {
          n.agents.slice(0, 3).forEach((agent, idx) => {
            const arch = agent.archetype || 'neutre'; const aColor = ARCHETYPE_ORBIT[arch]?.color || '#94a3b8';
            const nameText = agent.narrativeName || agent.displayName || agent.name;
            ctx.save(); ctx.font = '9px "DM Mono", Courier New, monospace'; ctx.fillStyle = aColor; ctx.globalAlpha = 0.7; ctx.textAlign = 'center';
            ctx.fillText(`· ${nameText}`, n.x, barY + 12 + idx * 11); ctx.restore();
          });
        } else {
          n.agents.forEach((agent, idx) => {
            const arch = agent.archetype || 'neutre'; const orbitCfg = ARCHETYPE_ORBIT[arch] || { speed: 0.5, radius: 1.0, pulse: false, color: '#94a3b8' };
            const orbitR = (size + 12) * orbitCfg.radius; const angle = time * orbitCfg.speed + (idx / (n.agents.length || 1)) * Math.PI * 2;
            const ox = n.x + Math.cos(angle) * orbitR; const oy = n.y + Math.sin(angle) * orbitR;
            const dotSize = orbitCfg.pulse ? 2 + Math.sin(time * 4 + idx) * 0.8 : 2;
            ctx.beginPath(); ctx.arc(ox, oy, dotSize, 0, Math.PI * 2); ctx.fillStyle = orbitCfg.color; ctx.shadowBlur = orbitCfg.pulse ? 6 : 3; ctx.shadowColor = orbitCfg.color; ctx.fill(); ctx.shadowBlur = 0;
          });
        }
      });
      const hudMetrics = [{ label: 'NODES', value: liveFamilies.length }, { label: 'LINKS', value: interactions.length }, { label: 'AGENTS', value: agents.length }, { label: 'TICK', value: localEngine?.currentTick || 0 }];
      const hudY = h - 14; const hudX0 = 16;
      hudMetrics.forEach((item, i) => {
        const x = hudX0 + i * 76;
        ctx.save(); ctx.globalAlpha = 0.35; ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(x - 4, hudY - 22, 68, 24); ctx.restore();
        drawTechLabel(x + 28, hudY - 11, item.label, 'rgba(255,255,255,0.22)', 7, 'center');
        drawTechLabel(x + 28, hudY + 1, String(item.value), palette.particle, 9, 'center');
      });
      const modeLabel = `${visualMode.toUpperCase()} / ${ambiance.toUpperCase()}`;
      ctx.save(); ctx.font = '7px "DM Mono", Courier New, monospace'; ctx.fillStyle = 'rgba(255,255,255,0.14)'; ctx.textAlign = 'right'; ctx.fillText(modeLabel, w - 14, 18); ctx.restore();
      animId = requestAnimationFrame(render);
    };
    const handleResize = () => {
      const dpr = window.devicePixelRatio || 1; const el = containerRef.current; if (!el) return;
      canvas.width = el.clientWidth * dpr; canvas.height = el.clientHeight * dpr; ctx.scale(dpr, dpr);
    };
    handleResize(); window.addEventListener('resize', handleResize); render();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', handleResize); };
  }, [neurons, interactions, theoryMode, darkMode, hoveredNode, isFullView, palette, tension, isFrozen, isChaotic, localEngine?.currentTick, getProcessMapPro, liveFamilies.length, agents.length, visualMode, ambiance]);

  const handleMouseMove = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect(); if (!rect) return;
    const x = e.clientX - rect.left; const y = e.clientY - rect.top;
    const found = neurons.find(n => {
      const nx = rect.width / 2 + n.baseX * rect.width; const ny = rect.height / 2 + n.baseY * rect.height;
      return Math.hypot(x - nx, y - ny) < 36;
    });
    setHoveredNode(found?.id || null); setHoveredFamily(found ? { name: found.name, narrativeRole: found.narrativeRole } : null);
  };

  return (
    <div ref={containerRef} className="w-full h-full relative overflow-hidden">
      <canvas ref={canvasRef} className="w-full h-full cursor-crosshair" onMouseMove={handleMouseMove}
        onMouseLeave={() => { setHoveredNode(null); setHoveredFamily(null); }}
        onClick={() => hoveredNode && onNodeClick?.(hoveredNode)} />
      <AnimatePresence>
        {hoveredFamily?.narrativeRole && (
          <motion.div key="tooltip" initial={{ opacity: 0, y: 6, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="absolute bottom-14 left-1/2 -translate-x-1/2 pointer-events-none z-20"
            style={{ maxWidth: '260px', padding: '10px 14px', borderRadius: '8px', background: 'rgba(6,5,14,0.92)', border: '1px solid rgba(167,139,250,0.15)', backdropFilter: 'blur(12px)', boxShadow: '0 4px 24px rgba(0,0,0,0.6)' }}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-1 h-1 rounded-full" style={{ background: FAMILY_COLORS[hoveredFamily.name] || '#a78bfa' }} />
              <span className="text-[9px] font-mono tracking-[0.2em] uppercase" style={{ color: FAMILY_COLORS[hoveredFamily.name] || '#a78bfa' }}>{hoveredFamily.name}</span>
            </div>
            <p className="text-[10px] leading-relaxed italic" style={{ color: 'rgba(203,213,225,0.75)', margin: 0 }}>{hoveredFamily.narrativeRole}</p>
          </motion.div>
        )}
      </AnimatePresence>
      {!isFullView && (
        <button onClick={() => window.open(`/field/${localEngine?.case?.slug}`, '_blank')}
          className="absolute top-3 right-3 transition-all duration-200 group"
          style={{ padding: '7px', borderRadius: '6px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', backdropFilter: 'blur(6px)' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(167,139,250,0.2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}>
          <Maximize2 size={12} style={{ color: 'rgba(255,255,255,0.4)' }} />
        </button>
      )}
    </div>
  );
};

const HUDStrip = ({ currentPhase, currentStep, currentStepIndex, scenario, tensionLevel, displayTime, maxActions, isConstraintReached, actionsThisPhase, showLearning, setShowLearning }) => {
  const HUD_AMBIANCE = {
    tension:    { text: 'text-red-400',     border: 'border-red-500/30',     dot: 'bg-red-400'     },
    hope:       { text: 'text-emerald-400', border: 'border-emerald-500/30', dot: 'bg-emerald-400' },
    crisis:     { text: 'text-amber-400',   border: 'border-amber-500/35',   dot: 'bg-amber-400'   },
    resolution: { text: 'text-indigo-400',  border: 'border-indigo-500/30',  dot: 'bg-indigo-400'  },
    default:    { text: 'text-violet-400',  border: 'border-violet-500/30',  dot: 'bg-violet-400'  },
  };
  const TENSION_LABELS = ['', 'STABLE', 'ACTIF', 'TENDU', 'CRITIQUE', 'RUPTURE'];
  const TENSION_COLORS = ['', 'bg-slate-500', 'bg-blue-500', 'bg-amber-500', 'bg-orange-500', 'bg-red-500'];
  const ambiance = HUD_AMBIANCE[currentPhase?.ambiance || 'default'];
  return (
    <div className="flex items-center gap-2 flex-wrap shrink-0 pointer-events-auto">
      {currentStep?.learningObjectives?.length > 0 && (
        <>
          <button onClick={() => setShowLearning(v => !v)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-black/35 border border-indigo-500/20 text-indigo-400/70 hover:text-indigo-300 hover:bg-black/50 transition-all backdrop-blur-md text-[10px] font-mono uppercase tracking-widest">
            <Brain size={10} />Objectifs d'apprentissage{showLearning ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
          </button>
          <AnimatePresence>
            {showLearning && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                <div className="px-5 py-4 rounded-xl bg-black/55 border border-indigo-500/15 backdrop-blur-xl">
                  <ul className="space-y-2">
                    {currentStep.learningObjectives.map((obj, i) => (
                      <li key={i} className="flex items-start gap-2 text-[11px] text-white/65 leading-relaxed">
                        <span className="text-indigo-400/60 shrink-0 font-mono">{String(i + 1).padStart(2, '0')}.</span>{obj}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-black/55 border ${ambiance.border} backdrop-blur-md`}>
        <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${ambiance.dot}`} />
        <span className={`text-[10px] font-mono tracking-widest uppercase ${ambiance.text}`}>{currentPhase?.type || 'INIT'}</span>
      </div>
      <div className="px-2.5 py-1 rounded-md bg-black/40 border border-white/10 backdrop-blur-md">
        <span className="text-[10px] font-mono text-white/55 uppercase tracking-widest">{String(currentStepIndex + 1).padStart(2, '0')}/{scenario?.steps?.length || 0}</span>
      </div>
      {currentStep?.narrativeArc?.position && (
        <div className="px-2.5 py-1 rounded-md bg-black/40 border border-violet-500/20 backdrop-blur-md">
          <span className="text-[10px] font-mono text-violet-400/75 uppercase tracking-widest">{currentStep.narrativeArc.position}</span>
        </div>
      )}
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-black/40 border border-white/30 backdrop-blur-md">
        <div className="flex gap-0.5 items-center">
          {[1,2,3,4,5].map(i => (
            <div key={i} className={`h-2.5 w-1 rounded-sm transition-all ${i <= tensionLevel ? TENSION_COLORS[tensionLevel] : 'bg-white/10'}`} />
          ))}
        </div>
        <span className="text-[9px] font-mono text-white/40 ml-0.5">{TENSION_LABELS[tensionLevel]}</span>
      </div>
      {maxActions !== null && (
        <div className={`px-2.5 py-1 rounded-md border backdrop-blur-md ${isConstraintReached ? 'bg-red-500/15 border-red-500/40 text-red-400' : 'bg-amber-500/10 border-amber-500/30 text-amber-400'}`}>
          <span className="text-[10px] font-mono uppercase tracking-widest">{isConstraintReached ? 'LIMITE' : `MAX ${maxActions}`}</span>
        </div>
      )}
      {displayTime !== null && (
        <div className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-md bg-black/55 border border-amber-500/30 backdrop-blur-md">
          <span className="text-[10px] font-mono text-amber-400/60 uppercase tracking-widest">T</span>
          <span className="text-sm font-bold font-mono text-amber-400 tabular-nums">{String(displayTime).padStart(2, '0')}s</span>
        </div>
      )}
    </div>
  );
};

const ActionCard = ({ action, index, isConstraintReached, onAction }) => (
  <motion.button
    initial={{ opacity: 0, x: -12 }} animate={{ opacity: isConstraintReached ? 0.4 : 1, x: 0 }}
    transition={{ delay: index * 0.07, duration: 0.25 }}
    whileHover={!isConstraintReached ? { x: 4, scale: 1.005 } : {}} whileTap={!isConstraintReached ? { scale: 0.995 } : {}}
    onClick={() => !isConstraintReached && onAction(action)} disabled={isConstraintReached}
    className={`group relative flex items-center justify-between w-full px-5 py-4 rounded-xl border transition-all duration-200 backdrop-blur-md text-left ${isConstraintReached ? 'bg-white/3 border-white/30 cursor-not-allowed' : 'bg-white/6 border-white/30 hover:border-amber-500/50 hover:bg-white/10 cursor-pointer'}`}>
    {!isConstraintReached && (
      <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(245,158,11,0.06), transparent 70%)' }} />
    )}
    <div className="flex items-center gap-4 min-w-0 relative">
      <span className="text-[11px] font-mono text-white/40 font-bold shrink-0 tabular-nums">{String(index + 1).padStart(2, '0')}</span>
      <div className="min-w-0">
        <p className="font-semibold text-[14px] text-white/90 leading-snug truncate">{action.label}</p>
        {action.competencySignal && (
          <p className="text-[10px] text-amber-400/65 mt-0.5 font-mono truncate">↳ {action.competencySignal.name} · {action.competencySignal.level}</p>
        )}
      </div>
    </div>
    <Zap size={13} className="opacity-0 group-hover:opacity-100 transition-opacity text-amber-400 shrink-0 ml-3" />
  </motion.button>
);

const BottomDrawer = ({ revealedDeltas, narrativeArc, currentStep, showLearning, setShowLearning }) => {
  const hasContent = revealedDeltas || narrativeArc?.nextStepForeshadow || currentStep?.learningObjectives?.length;
  if (!hasContent) return null;
  return (
    <div className="w-full max-w-2xl mx-auto space-y-2 pointer-events-auto mt-auto shrink-0">
      {narrativeArc?.previousStepLink && (
        <p className="text-center text-[11px] text-white/30 italic font-mono">← {narrativeArc.previousStepLink}</p>
      )}
      {narrativeArc?.nextStepForeshadow && (
        <div className="px-4 py-2.5 rounded-lg border border-white/30 bg-black/35 backdrop-blur-md text-center">
          <p className="text-[11px] text-white/40 leading-relaxed italic">→ {narrativeArc.nextStepForeshadow}</p>
        </div>
      )}
      <AnimatePresence>
        {revealedDeltas && (
          <motion.div key="reveal-delta" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
            className="px-5 py-4 rounded-xl border border-white/10 bg-black/55 backdrop-blur-xl">
            <p className="text-[10px] font-mono uppercase text-white/35 mb-3 tracking-widest flex items-center gap-2">
              <Activity size={9} /> Delta paramétrique — avant → après
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {Object.entries(revealedDeltas).filter(([, v]) => v.delta !== 0).sort((a, b) => Math.abs(b[1].delta) - Math.abs(a[1].delta)).map(([key, { before, after, delta }]) => (
                <div key={key} className="p-3 rounded-lg border border-white/30 bg-white/3 space-y-1.5">
                  <p className="text-[10px] font-mono uppercase text-white/40 truncate">{key}</p>
                  <div className="flex items-end gap-1.5">
                    <span className="text-lg font-bold text-white font-mono">{after}</span>
                    <span className={`text-[10px] font-bold pb-0.5 ${delta > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{delta > 0 ? '+' : ''}{delta}</span>
                  </div>
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${delta > 0 ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ width: `${after}%` }} />
                  </div>
                  <p className="text-[8px] font-mono text-white/25">{before} → {after}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const PESAFieldOverlay = ({ analyserRef, isAudioPlaying }) => {
  const { engine, currentStep, currentStepIndex, scenario, currentPhase, applyAction, completedActions, unlockedActions } = usePesa();
  const [showLearning, setShowLearning] = useState(false);
  const [revealedDeltas, setRevealedDeltas] = useState(null);
  const displayTime    = engine?.state?.remainingTime ?? null;
  const isActionPhase  = currentPhase?.type === 'action';
  const tensionLevel   = currentStep?.narrativeArc?.tensionLevel ?? 2;
  const narrativeArc   = currentStep?.narrativeArc;
  useEffect(() => {
    if (!currentPhase?.revealDelta) { setRevealedDeltas(null); return; }
    const init = engine?.state?.initialParameters || {}; const params = engine?.state?.parameters || {};
    const deltas = {}; Object.keys(params).forEach(k => { const before = init[k] ?? 50; const after = params[k] ?? 50; deltas[k] = { before, after, delta: after - before }; });
    setRevealedDeltas(deltas);
  }, [currentPhase?.revealDelta, engine?.state?.parameters]);
  const availableActions = useMemo(() => {
    if (currentPhase?.type !== 'action' || !currentPhase?.actionIds) return [];
    const safeUnlocked = unlockedActions instanceof Set ? unlockedActions : new Set();
    const safeCompleted = completedActions instanceof Set ? completedActions : new Set();
    return currentPhase.actionIds.map(item => {
      const id = typeof item === 'string' ? item : item.id;
      const action = currentStep?.actions?.find(a => a.id === id);
      if (!action || safeCompleted.has(id) || !safeUnlocked.has(id)) return null;
      return action;
    }).filter(Boolean);
  }, [currentPhase, currentStep, unlockedActions, completedActions]);
  const maxActions = currentPhase?.constraint === 'max-actions-1' ? 1 : null;
  const safeCompleted = completedActions instanceof Set ? completedActions : new Set();
  const actionsThisPhase = (currentPhase?.actionIds || []).filter(id => safeCompleted.has(typeof id === 'string' ? id : id.id)).length;
  const isConstraintReached = maxActions !== null && actionsThisPhase >= maxActions;
  const keyActionId = currentStep?.stepSuccessCriteria?.keyActionId;
  const keyActionDone = keyActionId ? safeCompleted.has(keyActionId) : true;
  const feedbackIfMissed = currentStep?.stepSuccessCriteria?.feedbackIfMissed;
  const handleAction = (action) => {
    applyAction(action.id);
    const agents = engine?.state?.agents || [];
    const reacting = agents.find(a => { if (a.reactionsTo instanceof Map) return a.reactionsTo.has(action.id); return !!a.reactionsTo?.[action.id]; });
    if (reacting) {
      const reaction = reacting.reactionsTo instanceof Map ? reacting.reactionsTo.get(action.id) : reacting.reactionsTo?.[action.id];
      const agentName = reacting.narrativeName || reacting.displayName || reacting.name;
      toast.custom(() => (
        <motion.div initial={{ opacity: 0, x: 40, scale: 0.95 }} animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, x: 40, scale: 0.95 }}
          className="max-w-sm w-full rounded-xl p-4 shadow-2xl backdrop-blur-md flex items-start gap-3"
          style={{ background: 'rgba(10,10,18,0.95)', border: '1px solid rgba(245,158,11,0.25)' }}>
          <div className="shrink-0 w-9 h-9 rounded-full border border-amber-500/30 bg-amber-500/10 flex items-center justify-center">
            <span className="text-sm font-bold text-amber-400">{agentName.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wide truncate">{agentName}</p>
              {reacting.archetype && <span className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-white/45 bg-white/5">{reacting.archetype}</span>}
            </div>
            <p className="text-[12px] text-white/75 leading-relaxed italic">"{reaction}"</p>
          </div>
        </motion.div>
      ), { duration: 5500, position: 'bottom-right' });
    }
  };
  return (
    <div className="relative w-full h-full flex flex-col pointer-events-none gap-4 p-6 overflow-y-auto no-scrollbar">
      <div className="pointer-events-auto shrink-0">
        <HUDStrip currentPhase={currentPhase} currentStep={currentStep} currentStepIndex={currentStepIndex} scenario={scenario} tensionLevel={tensionLevel} displayTime={displayTime} maxActions={maxActions} isConstraintReached={isConstraintReached} actionsThisPhase={actionsThisPhase} showLearning={showLearning} setShowLearning={setShowLearning} />
      </div>
      <div className="pointer-events-auto shrink-0">
        <div className="px-5 py-3 rounded-xl bg-black/65 border border-white/10 backdrop-blur-md max-w-2xl">
          {currentPhase?.title && <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest mb-1">{currentPhase.title}</p>}
          <h2 className="text-xl font-semibold text-white/92 leading-snug tracking-tight">{currentStep?.title || 'ANALYSE EN COURS'}</h2>
        </div>
      </div>
      <div className="flex-1 flex flex-col justify-center items-center max-w-2xl mx-auto w-full z-10 gap-4">
        <AnimatePresence mode="wait">
          {!isActionPhase && (
            <motion.div key="narrative" initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -16, scale: 0.98 }} transition={{ duration: 0.35, ease: 'easeOut' }} className="w-full pointer-events-auto">
              <div className="p-8 rounded-2xl border backdrop-blur-xl text-center relative overflow-hidden" style={{ background: 'rgba(5,5,14,0.80)', borderColor: 'rgba(255,255,255,0.10)', boxShadow: '0 0 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)' }}>
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-500/45" />
                  <Activity className="text-amber-400/80" size={20} />
                  <div className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-500/45" />
                </div>
                <p className="text-[18px] font-light leading-relaxed text-white/88 mb-5 px-2">{currentStep?.narrativeOverride || currentPhase?.narrative || currentStep?.description || 'Le système évolue…'}</p>
                {currentPhase?.narrative && currentPhase.narrative !== currentStep?.narrativeOverride && (
                  <p className="text-[13px] text-white/50 leading-relaxed italic mb-5 px-4">{currentPhase.narrative}</p>
                )}
                <div className="w-full h-10 max-w-sm mx-auto relative mb-4">
                  <div className="absolute inset-0 bg-amber-500/4 rounded-lg" />
                  <K2000VoiceScanner isActive analyserRef={analyserRef} darkMode />
                </div>
                <div className="flex justify-center items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse bg-amber-400/70" />
                  <span className="text-[10px] font-mono text-white/35 uppercase tracking-wider">Signal Stable</span>
                </div>
              </div>
              {currentPhase?.theoreticalHook && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                  className="mt-3 px-5 py-4 rounded-xl border border-indigo-500/20 bg-indigo-500/6 backdrop-blur-md">
                  <p className="text-[10px] font-mono text-indigo-400/65 uppercase mb-1.5 flex items-center gap-2 tracking-widest"><BookOpen size={10} /> Ancrage théorique</p>
                  <p className="text-[12px] text-indigo-200/80 leading-relaxed italic">{currentPhase.theoreticalHook}</p>
                </motion.div>
              )}
            </motion.div>
          )}
          {isActionPhase && (
            <motion.div key="action-zone" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full space-y-3 pointer-events-auto">
              {currentStep?.problematisation && (
                <div className="px-6 py-5 rounded-xl border text-center backdrop-blur-md" style={{ background: 'rgba(5,5,14,0.75)', borderColor: 'rgba(245,158,11,0.22)' }}>
                  <p className="text-[16px] font-light leading-relaxed italic text-white/88">"{currentStep.problematisation}"</p>
                  {currentStep.epistemologicalQuestion && (
                    <div className="mt-3 text-[10px] font-mono uppercase tracking-wide py-1.5 px-4 rounded-lg border border-dashed border-violet-400/30 text-violet-300/80 bg-violet-500/6 inline-block">{currentStep.epistemologicalQuestion}</div>
                  )}
                </div>
              )}
              {currentPhase?.hint && !isConstraintReached && (
                <div className="px-4 py-2.5 rounded-lg border border-white/30 bg-black/30 backdrop-blur-md flex items-start gap-2.5">
                  <Lightbulb size={11} className="text-amber-400/65 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-white/55 leading-relaxed italic">{currentPhase.hint}</p>
                </div>
              )}
              <div className="space-y-2">
                {availableActions.map((action, idx) => (
                  <ActionCard key={action.id} action={action} index={idx} isConstraintReached={isConstraintReached} onAction={handleAction} />
                ))}
                {availableActions.length === 0 && !isConstraintReached && (
                  <div className="text-center py-6"><p className="text-[13px] font-mono text-white/30">Aucune action disponible.</p></div>
                )}
              </div>
              <button onClick={() => engine?.advancePhase()} className="w-full mt-1 py-3.5 rounded-xl font-semibold text-[13px] flex items-center justify-center gap-3 transition-all border"
                style={{ background: 'rgba(124,58,237,0.75)', borderColor: 'rgba(139,92,246,0.5)', color: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.9)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(124,58,237,0.75)'}>
                CONTINUER → ÉTAPE SUIVANTE
              </button>
              {!keyActionDone && feedbackIfMissed && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-4 py-3 rounded-lg border border-amber-500/25 bg-amber-500/6 flex items-start gap-2.5">
                  <AlertTriangle size={11} className="text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-300/80 leading-relaxed">{feedbackIfMissed}</p>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <BottomDrawer revealedDeltas={revealedDeltas} narrativeArc={narrativeArc} currentStep={currentStep} showLearning={showLearning} setShowLearning={setShowLearning} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LiveFieldModal (inchangé)
// ─────────────────────────────────────────────────────────────────────────────

const FieldHUD = ({ state, caseData, onClose, isProjection }) => {
  const phase = state?.currentPhase; const stepIdx = state?.currentStepIndex ?? 0; const step = caseData?.steps?.[stepIdx];
  const tension = step?.narrativeArc?.tensionLevel ?? 2;
  const tensionColors = ['', '#64748b', '#3b82f6', '#f59e0b', '#f97316', '#ef4444'];
  const tensionLabels = ['', 'STABLE', 'ACTIF', 'TENDU', 'CRITIQUE', 'RUPTURE'];
  const tColor = tensionColors[tension] || '#a78bfa';
  return (
    <>
      <div className="absolute top-0 left-0 right-0 z-40 flex items-center justify-between px-8 py-4"
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, transparent 100%)' }}>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
            </span>
            <span className="text-[9px] font-mono tracking-[0.25em] uppercase text-amber-400/70">Champ Vivant — Live</span>
          </div>
          <div className="h-3 w-px bg-white/10" />
          <span className="text-[11px] font-semibold text-white/70 truncate max-w-[300px]">{caseData?.title || 'Simulation'}</span>
        </div>
        <div className="flex items-center gap-2">
          {isProjection && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[9px] font-mono tracking-widest uppercase"
              style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(165,180,252,0.8)' }}>
              <Maximize2 size={9} />Projection
            </div>
          )}
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg transition-all"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}>
            <X size={14} className="text-white/60" />
          </button>
        </div>
      </div>
      {step && (
        <div className="absolute bottom-8 left-8 z-40 max-w-sm pointer-events-none"
          style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', borderRadius: '10px', padding: '12px 16px' }}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[8px] font-mono tracking-widest uppercase text-white/20">STEP {String(stepIdx + 1).padStart(2, '0')}</span>
            {phase?.type && <><span className="text-white/10">·</span><span className="text-[8px] font-mono tracking-widest uppercase text-white/20">{phase.type}</span></>}
          </div>
          <p className="text-sm font-semibold text-white/80 leading-tight">{step.title}</p>
          {phase?.theoreticalHook && <p className="text-[9px] text-indigo-300/50 italic leading-snug mt-1.5">{phase.theoreticalHook}</p>}
        </div>
      )}
      <div className="absolute bottom-8 right-8 z-40 pointer-events-none"
        style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', borderRadius: '10px', padding: '10px 14px' }}>
        <div className="text-[8px] font-mono tracking-widest uppercase text-white/20 mb-2">Tension</div>
        <div className="flex items-end gap-1 mb-1.5">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="w-2 rounded-sm transition-all duration-300" style={{ height: `${8 + i * 3}px`, background: i <= tension ? tColor : 'rgba(255,255,255,0.06)', boxShadow: i <= tension ? `0 0 6px ${tColor}60` : 'none' }} />
          ))}
        </div>
        <div className="text-[8px] font-mono text-right" style={{ color: `${tColor}80` }}>{tensionLabels[tension]}</div>
      </div>
    </>
  );
};

export const LiveFieldModal = ({ isOpen, onClose }) => {
  const { engine } = usePesa();
  const [isProjection, setIsProjection] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler); return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);
  useEffect(() => { document.body.style.overflow = isOpen ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [isOpen]);
  if (!engine?.state) return null;
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div key="live-field-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }}
          className="fixed inset-0 z-[200] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
          <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden"
            style={{ width: isProjection ? '100vw' : 'calc(100vw - 80px)', height: isProjection ? '100vh' : 'calc(100vh - 80px)', borderRadius: isProjection ? '0' : '20px', border: isProjection ? 'none' : '1px solid rgba(255,255,255,0.08)', boxShadow: '0 0 120px rgba(0,0,0,0.9)', background: '#050505', transition: 'width 0.35s ease, height 0.35s ease, border-radius 0.35s ease' }}>
            <PESAField externalState={{ ...engine.state, theoryMode: engine.theoryMode }} isFullView={true} />
            <FieldHUD state={engine.state} caseData={engine.case} onClose={onClose} isProjection={isProjection} />
            <button onClick={() => setIsProjection(v => !v)}
              className="absolute top-4 left-48 z-50 flex items-center justify-center w-8 h-8 rounded-lg transition-all"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.15)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}>
              {isProjection ? <Minimize2 size={12} className="text-white/50" /> : <Shrink size={12} className="text-white/50" />}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// HOOKS COMPATIBILITÉ
// ─────────────────────────────────────────────────────────────────────────────

export const useActionPulse = (watchedPanels = []) => {
  const { completedActions } = usePesa();
  const [pulsingPanels, setPulsingPanels] = useState({});
  const prevCountRef = useRef(0);
  const completedArr = completedActions instanceof Set ? [...completedActions] : [];
  useEffect(() => {
    if (completedArr.length > prevCountRef.current) {
      const next = {}; watchedPanels.forEach(p => { next[p] = Date.now(); });
      setPulsingPanels(next); prevCountRef.current = completedArr.length;
      const t = setTimeout(() => setPulsingPanels({}), 2400); return () => clearTimeout(t);
    }
  }, [completedArr.length]);
  const isPulsing = (panel) => !!pulsingPanels[panel];
  return { isPulsing };
};

export const PulsingNavButton = ({ active, icon: Icon, label, onClick, pulsing = false }) => (
  <button onClick={onClick}
    className={`relative group flex items-center gap-2.5 w-full px-3 py-2.5 rounded-xl text-[11px] font-mono tracking-wider uppercase transition-all duration-200 border ${active ? 'bg-amber-500/12 border-amber-500/35 text-amber-300' : 'bg-white/3 border-white/30 text-white/50 hover:text-white/80 hover:bg-white/6 hover:border-white/15'}`}>
    {pulsing && (
      <>
        <span className="absolute inset-0 rounded-xl animate-[ping_0.8s_ease-out_3] border-2 border-amber-400/60 pointer-events-none" />
        <span className="absolute inset-0 rounded-xl bg-amber-500/10 pointer-events-none" />
      </>
    )}
    <Icon size={13} className={pulsing ? 'text-amber-400' : ''} />
    <span>{label}</span>
    {pulsing && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />}
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANTS AUXILIAIRES PAGE
// ─────────────────────────────────────────────────────────────────────────────

const StartScreen = ({ scenario, engine, darkMode, audioRef, analyserRef, isAudioPlaying, setIsAudioPlaying, initAudioAnalysis }) => (
    <div 
      className="fixed inset-0 bg-gradient-to-br from-slate-950 via-black to-slate-900 z-[1000] flex items-center justify-center overflow-hidden"
      onMouseEnter={initAudioAnalysis} 
      onClick={initAudioAnalysis}
    >
      {/* Background field */}
      <div className="fixed inset-0 z-0 opacity-30">
        <PESAField />
      </div>

      {/* Subtle grid lab overlay */}
      <div 
        className="absolute inset-0 opacity-[0.06]" 
        style={{
          backgroundImage: `
            linear-gradient(#64748b 0.8px, transparent 1px), 
            linear-gradient(90deg, #64748b 0.8px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }} 
      />

      <motion.div 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        className="relative z-10 w-full max-w-5xl p-12 border border-white/5 bg-black/70 backdrop-blur-2xl rounded-3xl shadow-2xl"
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-12 border-b border-white/5 pb-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2.5 h-2.5 rounded-full bg-purple-400 animate-pulse" />
              <h2 className="text-purple-400 text-xs tracking-[3px] uppercase font-medium">SIGNAL DÉTECTÉ • LIVE</h2>
            </div>
            <h1 className="text-6xl font-light text-white tracking-tighter">
              {scenario.title}
            </h1>
          </div>

          <div className="text-right space-y-1">
            <p className="text-xs text-slate-400 uppercase tracking-widest">
              Status: <span className="text-amber-400 font-medium">En attente d'initialisation</span>
            </p>
            <p className="text-xs text-purple-400/70 font-medium">Protocole • PESACORE</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left column - Visualizer + Goal */}
          <div className="space-y-8">
            <div className="p-8 bg-black/50 border border-white/5 rounded-2xl backdrop-blur-xl">
              <div className="flex items-center justify-between mb-6">
                <p className="text-xs text-purple-300/60 uppercase tracking-widest">Analyse spectrale en temps réel</p>
                <div className={`w-2 h-2 rounded-full ${isAudioPlaying ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'}`} />
              </div>
              
              <SignalVisualizer 
                analyserRef={analyserRef} 
                isActive={isAudioPlaying} 
              />

              <audio 
                ref={audioRef} 
                src="/audio/intro-pesa.mp3" 
                onPlay={() => setIsAudioPlaying(true)} 
                onPause={() => setIsAudioPlaying(false)} 
                onEnded={() => setIsAudioPlaying(false)} 
                autoPlay 
              />
            </div>

            <div className="pl-1">
              <p className="text-slate-300 leading-relaxed italic border-l-2 border-purple-500/30 pl-6 text-[15px]">
                "{scenario.pedagogicalGoal}"
              </p>
            </div>
          </div>

          {/* Right column - Action */}
          <div className="flex flex-col items-center justify-center space-y-8">
            <button 
              onClick={() => {
                initAudioAnalysis();
                if (engine.state.currentPhase?.duration > 0) {
                  engine._schedulePhaseAdvance(engine.state.currentPhase.duration);
                }
                engine.state.isPhaseRunning = true;
                engine._notifyStore();
              }}
              className="group relative w-full py-14 border border-purple-500/30 hover:border-purple-400 bg-black hover:bg-purple-950/30 transition-all duration-700 overflow-hidden rounded-2xl"
            >
              {/* Glow layer */}
              <div className="absolute inset-0 bg-gradient-to-b from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <span className="relative flex items-center justify-center gap-5 text-2xl font-light tracking-tight text-purple-300 group-hover:text-white transition-colors">
                <Zap size={32} className="group-hover:rotate-12 transition-transform duration-500" />
                INITIALISER LE CHAMP VIVANT
              </span>
            </button>

            <p className="text-[11px] text-purple-500/30 uppercase tracking-[2px] text-center">
              Simulation systémique • Irréversible
            </p>
          </div>
        </div>
      </motion.div>
    </div>
);



const LoadingScreen = () => (
  <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-gradient-to-br from-black via-slate-950 to-black">
    <div className="flex flex-col items-center gap-8">
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} className="w-20 h-20 border-4 border-purple-600 border-t-transparent rounded-full" />
      <span className="font-mono text-[10px] tracking-wide uppercase opacity-50">Synchronisation moteur PESACORE</span>
    </div>
  </div>
);

const HeaderTool = ({ icon: Icon, onClick, active }) => (
  <button onClick={onClick}
    className={`p-3 rounded-lg transition-all backdrop-blur-sm ${active ? 'bg-white/10 text-amber-400 border border-white/10' : 'text-slate-400 hover:bg-white/5 border border-transparent hover:border-white/5'}`}>
    <Icon size={18} />
  </button>
);

const SpecialButton = ({ label, icon: Icon, color, active, onClick }) => {
  const styles = {
    purple: { base: 'border-violet-500/15 text-violet-400/60 bg-transparent', hover: 'hover:bg-violet-500/10 hover:text-violet-300 hover:border-violet-500/25', activeC: 'bg-violet-600/20 text-violet-300 border-violet-500/30' },
    amber:  { base: 'border-amber-500/15  text-amber-400/60  bg-transparent', hover: 'hover:bg-amber-500/10  hover:text-amber-300  hover:border-amber-500/25',  activeC: 'bg-amber-500/20  text-amber-300  border-amber-500/30'  },
  };
  const s = styles[color] || styles.purple;
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border text-[11px] font-mono tracking-wide transition-all duration-150 ${active ? s.activeC : `${s.base} ${s.hover}`}`}>
      <Icon size={13} className={active ? '' : 'opacity-60'} />
      <span className="flex-1 text-left">{label}</span>
      {active && <div className="w-1 h-1 rounded-full bg-current opacity-70" />}
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PAGE PRINCIPALE — GenericCase
// ─────────────────────────────────────────────────────────────────────────────

export default function GenericCase() {
  const {
    engine, loadSimulation, restartSimulation,
    toggleTheoryMode, phase, scenario,
    getProcessMapPro, theoryMode,
    closeDebrief, showDebrief, currentSolution,
    closeSolution,
  } = usePesa(); 

  const { slug }     = useParams();
  const { darkMode } = useTheme();
  const navigate     = useNavigate();

  const [loading, setLoading]               = useState(true);
  const [soundEnabled, setSoundEnabled]     = useState(true);
  const [leftCollapsed, setLeftCollapsed]   = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(true);
  const [fieldModalOpen, setFieldModalOpen] = useState(false);


  // [4] Debrief onboarding — seul point d'entrée vers RapportEnquete
  const [showDebriefOnboarding, setShowDebriefOnboarding] = useState(false);
  const [showFinalReport, setShowFinalReport]             = useState(false);
  
  const onboardingFiredRef = useRef(false);

  /**
   * Quand le store signale showDebrief=true (déclenché par applyAction après isFinished),
   * on intercède : on ouvre l'onboarding à la place du rapport.
   * closeDebrief() est appelé immédiatement pour remettre showDebrief=false dans le store
   * et éviter tout double-rendu du RapportEnquete.
   */
  useEffect(() => {
    if (showDebrief && !onboardingFiredRef.current) {
      onboardingFiredRef.current = true;
      closeDebrief();                        // remet showDebrief=false dans le store
      setShowDebriefOnboarding(true);        // on prend la main : onboarding d'abord
    }
    // Quand la simulation redémarre, on réarme le verrou
    if (!showDebrief && phase !== 'finished') {
      onboardingFiredRef.current = false;
    }
  }, [showDebrief, closeDebrief, phase]);

  const audioRef       = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef    = useRef(null);
  const sourceRef      = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  const initAudioAnalysis = async () => {
    if (audioContextRef.current) {
      if (audioContextRef.current.state === 'suspended') await audioContextRef.current.resume();
      return;
    }
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = ctx.createAnalyser(); analyser.fftSize = 256;
    try {
      const source = ctx.createMediaElementSource(audioRef.current);
      source.connect(analyser); analyser.connect(ctx.destination);
      audioContextRef.current = ctx; analyserRef.current = analyser; sourceRef.current = source;
    } catch (e) { console.error("AudioSource error:", e); }
  };

  useEffect(() => {
    const fetchCase = async () => {
      try {
        const res = await api.get(`/cases/generic/${slug}`);
        loadSimulation(res.data); setLoading(false);
      } catch (err) { toast.error("Échec de l'initialisation du moteur"); navigate('/dashboard'); }
    };
    if (slug && !engine?.case) fetchCase();
    else if (engine?.case) setLoading(false);
  }, [slug, engine?.case, loadSimulation, navigate]);

  useEffect(() => {
    if (engine?.state && slug) {
      const syncPayload = { state: engine.state, case: engine.case, theoryMode, processMapPro: getProcessMapPro(), scenario, lastUpdate: Date.now() };
      localStorage.setItem(`pesa_sync_${slug}`, JSON.stringify(syncPayload));
    }
  }, [engine?.state, theoryMode, slug, getProcessMapPro, scenario]);

  const progress = useMemo(() => {
    if (!scenario?.steps?.length) return 0;
    return Math.min(100, Math.round(((engine?.state?.currentStepIndex || 0) / (scenario.steps.length - 1)) * 100));
  }, [scenario, engine?.state?.currentStepIndex]);

  const handleDebriefComplete = () => {
    setShowDebriefOnboarding(false);
    
    // Vérifier si la solution est prête
    const checkSolution = () => {
      if (engine?.state?.currentSolution) {
        setShowFinalReport(true);
      } else {
        // Réessayer dans 100ms
        setTimeout(checkSolution, 100);
      }
    };
    
    checkSolution();
  };

  if (loading || !scenario) return <LoadingScreen />;

  if (window.innerWidth < 1024) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#0a0a0a] text-white text-center">
        <div className="max-w-md">
          <div className="text-6xl mb-6">💻</div>
          <h2 className="text-2xl font-bold mb-4">Simulation optimisée pour ordinateur</h2>
          <p className="opacity-70 mb-8">Cette expérience pédagogique est conçue pour être utilisée sur un écran ≥ 1024px.</p>
          <button onClick={() => window.history.back()} className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl border border-white/20">Retour</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen font-sans selection:bg-amber-500/30 transition-colors duration-1000 ${darkMode ? 'bg-[#0a0a0a] text-white' : 'bg-slate-50 text-slate-900'}`}>
      <LabGrid darkMode={darkMode} />
      <Scanlines />

      {/* [3] InsightOverlay — rendu global, indépendant */}
      <InsightOverlay />

      <div className="fixed inset-0 z-0 opacity-30"><PESAField /></div>

      <div className="relative z-10 flex flex-col h-screen">

        {engine&&!engine.state.isPhaseRunning&&(
        <StartScreen
          scenario={scenario} engine={engine}
          audioRef={audioRef} analyserRef={analyserRef}
          isAudioPlaying={isAudioPlaying} setIsAudioPlaying={setIsAudioPlaying}
          initAudioAnalysis={initAudioAnalysis}
        />
      )}

        {/* HEADER */}
        <header className={`shrink-0 sticky top-0 z-[60] backdrop-blur-xl border-b transition-all duration-300 ${darkMode ? 'bg-black/80 border-white/10' : 'bg-white/80 border-slate-200'}`}>
          <div className="max-w-[1920px] mx-auto px-6 h-20 flex items-center gap-6">

            {/* Gauche */}
            <div className="flex items-center gap-5 flex-1 min-w-0">
              <button onClick={() => navigate('/dashboard')}
                className={`p-2.5 rounded-xl border transition-all group shrink-0 ${darkMode ? 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20' : 'bg-slate-100 border-slate-200 hover:bg-slate-200'}`}>
                <ChevronLeft size={20} className="group-hover:-translate-x-0.5 transition-transform" />
              </button>
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-amber-500/10 text-amber-500 border border-amber-500/20 uppercase tracking-widest shrink-0">PESACORE Simulation</span>
                  <h1 className="text-lg font-bold truncate tracking-tight">{scenario.title}</h1>
                </div>
                <p className="text-[11px] opacity-50 font-medium truncate max-w-[300px] mt-0.5">{scenario.pedagogicalGoal}</p>
              </div>
            </div>

            {/* Centre : phase + progression + [2] ProbabilityBridge */}
            <div className="flex flex-col items-center gap-2.5 flex-1 hidden md:flex">
              <div className="flex items-center gap-3">
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] font-bold tracking-tighter uppercase transition-colors ${phase === 'playing' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' : 'bg-amber-500/10 border-amber-500/20 text-amber-500'}`}>
                  <span className="relative flex h-2 w-2">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${phase === 'playing' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${phase === 'playing' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                  </span>
                  {phase}
                </div>
                <div className="h-4 w-px bg-white/10" />
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-bold opacity-40 uppercase tracking-widest">Progression</span>
                  <div className="w-32 h-1.5 rounded-full overflow-hidden bg-white/5 border border-white/5">
                    <motion.div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500" initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.8, ease: "circOut" }} />
                  </div>
                  <span className="text-xs font-mono font-bold w-8">{progress}%</span>
                </div>
              </div>

              {/* [2] ProbabilityBridge dans le header */}
              <div className="w-full max-w-xs">
                <ProbabilityBridge />
              </div>
            </div>

            {/* Droite */}
            <div className="flex items-center justify-end gap-3 flex-1">
              <div className="flex items-center gap-1.5 p-1 rounded-xl bg-black/20 border border-white/5">
                <HeaderTool icon={soundEnabled ? Volume2 : VolumeX} onClick={() => setSoundEnabled(!soundEnabled)} />
                <HeaderTool icon={darkMode ? Sun : Moon} active={darkMode} />
              </div>
              <button onClick={restartSimulation}
                className="group flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white transition-all text-[11px] font-bold border border-red-500/20">
                <History size={14} className="group-hover:rotate-[-45deg] transition-transform" />
                REINITIALISER
              </button>
            </div>

          </div>
        </header>

        {/* GRID PRINCIPAL */}
        <main className="flex-1 grid gap-0 overflow-hidden relative"
          style={{
            gridTemplateColumns: [
              leftCollapsed  ? '0px' : 'minmax(0,1fr)',
              'minmax(0,2fr)',
              rightCollapsed ? '0px' : 'minmax(0,1fr)',
            ].join(' '),
            transition: 'grid-template-columns 350ms cubic-bezier(0.4,0,0.2,1)',
          }}
        >

          {/* ═══════════════════════════
              COLONNE GAUCHE — [1] ContextStream
          ═══════════════════════════ */}
          <div className="flex flex-col h-full overflow-hidden"
            style={{
              opacity:       leftCollapsed ? 0 : 1,
              pointerEvents: leftCollapsed ? 'none' : 'auto',
              transition:    'opacity 200ms ease',
            }}
          >
            {/* [1] Context Stream — remplace les boutons nav */}
            <div
              className="flex-1 overflow-hidden"
              style={{
                borderRight: `1px solid ${T.border0}`,
              }}
            >
              <ContextStream />
            </div>

            {/* Outils avancés — conservés en bas */}
            <div
              className="shrink-0 p-4 space-y-2 border-t"
              style={{ borderColor: T.border0 }}
            >
              <span className={`${MONO_SM} text-white/20 px-1 block`}>Outils</span>
              <SpecialButton label="Champ Vivant" icon={Zap} color="purple" onClick={() => window.open(`/field/${scenario?.slug || 'default'}`, '_blank')} />
              <SpecialButton label="Vue Modale"   icon={Maximize2} color="purple" onClick={() => setFieldModalOpen(true)} />
              <SpecialButton label={`Théorie: ${theoryMode ? 'ON' : 'OFF'}`} icon={School} color="amber" active={theoryMode} onClick={toggleTheoryMode} />
              <SpecialButton label="Visualisation 3D" icon={Box} color="purple" onClick={() => window.open(`/viz3d/generic/${slug}`, '_blank')} />
            </div>
          </div>

          {/* ═══════════════════════════
              COLONNE CENTRALE
          ═══════════════════════════ */}
          <div className="flex flex-col gap-6 overflow-hidden relative p-8 px-4">

            {/* Toggle LEFT */}
            <button onClick={() => setLeftCollapsed(v => !v)}
              className={`absolute left-0 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-5 h-14 rounded-r-lg border-y border-r transition-all duration-200 backdrop-blur-md ${leftCollapsed ? 'bg-purple-600/80 border-purple-500/60 text-white hover:bg-purple-500' : 'bg-black/40 border-white/30 text-white/30 hover:text-white hover:bg-black/60'}`}>
              {leftCollapsed ? <PanelLeftOpen size={12} /> : <PanelLeftClose size={12} />}
            </button>

            {/* Toggle RIGHT */}
            <button onClick={() => setRightCollapsed(v => !v)}
              className={`absolute right-0 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-5 h-14 rounded-l-lg border-y border-l transition-all duration-200 backdrop-blur-md ${rightCollapsed ? 'bg-purple-600/80 border-purple-500/60 text-white hover:bg-purple-500' : 'bg-black/40 border-white/30 text-white/30 hover:text-white hover:bg-black/60'}`}>
              {rightCollapsed ? <PanelRightOpen size={12} /> : <PanelRightClose size={12} />}
            </button>

            <div className="relative flex-1 rounded-[3rem] overflow-hidden border shadow-2xl transition-all duration-500 bg-black/10 border-white/5 backdrop-blur-sm mx-5">
              <PESAFieldOverlay analyserRef={analyserRef} isAudioPlaying={isAudioPlaying} />
            </div>
          </div>

          {/* ═══════════════════════════
              COLONNE DROITE — Radar + Solutions
          ═══════════════════════════ */}
          <div className="flex flex-col gap-6 overflow-hidden"
            style={{
              opacity:       rightCollapsed ? 0 : 1,
              pointerEvents: rightCollapsed ? 'none' : 'auto',
              transition:    'opacity 200ms ease',
              padding:       rightCollapsed ? 0 : '2rem',
              paddingLeft:   rightCollapsed ? 0 : '1rem',
            }}
          >
            <div className="shrink-0"><RadialParameters/></div>
            <div className="shrink-0"><SolutionsPanel onClose={closeSolution} /></div>
          </div>

          <LiveFieldModal isOpen={fieldModalOpen} onClose={() => setFieldModalOpen(false)} />

        </main>

        {/*
          Bouton "Consulter le rapport" — visible en phase finished.
          Permet à l'utilisateur de rouvrir l'onboarding s'il l'a fermé,
          ou d'accéder directement au rapport s'il a déjà traversé l'onboarding.
        */}
        {phase === 'finished' && !showDebriefOnboarding && !showFinalReport && (
          <motion.button
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowDebriefOnboarding(true)}
            className="fixed bottom-12 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-full font-semibold flex items-center gap-4 shadow-lg border"
            style={{
              background: `linear-gradient(135deg, #7c3aed, ${T.gold})`,
              borderColor: 'rgba(255,255,255,0.15)',
              color: '#fff',
              boxShadow: `0 0 40px rgba(124,58,237,0.5), 0 0 80px rgba(201,165,90,0.2)`,
            }}
          >
            <Sparkles size={18} className="animate-pulse" />
            CONSULTER LE RAPPORT
            <FileText size={18} />
          </motion.button>
        )}

        <Toaster position="bottom-right" />

        {/*
          [4] DebriefOnboarding
          onComplete → ferme l'onboarding, ouvre le rapport local (showFinalReport)
          onClose    → ferme l'onboarding, le bouton réapparaît
        */}
        <AnimatePresence>
          {showDebriefOnboarding && (
            <DebriefOnboarding
              onComplete={handleDebriefComplete}
              onClose={() => setShowDebriefOnboarding(false)}
            />
          )}
        </AnimatePresence>

        {/*
          RapportEnquete — rendu UNIQUEMENT via showFinalReport (jamais via showDebrief du store).
          closeDebrief est appelé à la fermeture pour nettoyer l'état du store si besoin.
        */}
        {showFinalReport && (
          <RapportEnquete
            onClose={() => {
              setShowFinalReport(false);
              closeDebrief();
            }}
          />
        )}

      </div>
    </div>
  );
}